/**
 * useStreamSession — all live streaming state (orchestrator, Phase 4 PRI-291)
 * Phase 1: camera access, device enumeration, state machine
 * Phase 2: WebRTC + WebSocket wired in via dynamic import (webrtc.ts, stream-signalling.ts)
 * Phase 4: State extracted into sub-hooks (stream/ directory); orchestrator delegates state ownership.
 *          Sub-hooks: useCamera, useStreamMetering, useStreamNarrative, useStreamRecording,
 *                     useSignalling, useWebRTCConnection, useFileStream, usePreConnect
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { getTelemetry, type TelemetrySeverity } from '../telemetry/TelemetryProvider'

// R14 (PRI-439): Sentry-shaped local shim delegating to the injected
// TelemetryErrorReporter — core must not import @sentry/react. All 22
// existing call sites keep their exact shapes; the host app injects the
// real Sentry-backed reporter (src/primate/telemetry-bridge).
const Sentry = {
  addBreadcrumb(crumb: { category: string; message: string; level?: TelemetrySeverity; data?: Record<string, unknown> }): void {
    getTelemetry().reporter.addBreadcrumb(crumb)
  },
  captureMessage(message: string, opts?: { level?: TelemetrySeverity; tags?: Record<string, string>; extra?: Record<string, unknown> }): void {
    getTelemetry().reporter.captureMessage(message, opts)
  },
  captureException(error: unknown, opts?: { tags?: Record<string, string>; extra?: Record<string, unknown> }): void {
    getTelemetry().reporter.captureException(error, opts)
  },
}
import { useStreamMetering, INITIAL_METERING } from './stream/useStreamMetering'
import { useStreamNarrative } from './stream/useStreamNarrative'
import { useStreamRecording } from './stream/useStreamRecording'
import { useCamera } from './stream/useCamera'
import { useSignalling } from './stream/useSignalling'
import { useWebRTCConnection } from './stream/useWebRTCConnection'
import { useFileStream } from './stream/useFileStream'
import { usePreConnect } from './stream/usePreConnect'
import {
  type ConnectionState,
  type StreamSessionState,
  type StreamSessionActions,
  type StreamMetrics,
  type StreamQueueState,
  type LiveInferenceResult,
} from './types'
import type { NarrativeEntry } from '../lib/narrative'
import { getStreamSignalUrl, getTurnCredentials, getStreamConfig, type TurnResult } from '../lib/api/stream'
import { SignallingClient, type ISignallingClient, type SignallingClientOptions, type SignallingDiagnostics } from '../lib/stream-signalling'
// PRI-496 Lane B: public streams plane (POST /v1/streams + versioned signal WS)
// behind the stream-plane kill-switch — default public, instant legacy rollback.
import { PublicStreamSignalling } from '../lib/api/public-stream-signalling'
import { resolveStreamPlane } from '../lib/api/stream-plane'
import { WebRTCClient } from '../lib/webrtc'
import { sdk } from '../lib/api/sdk'
import { stripCompiledQueryEnvelope, type PillUsage } from '../components/v2/pv2-shared'
import { streamErrorDisplayInput } from '../components/v2/V2StreamOverlay'
import {
  trackStreamStarted,
  trackStreamQueued,
  trackStreamLive,
  trackStreamStopped,
  trackStreamQuotaWarning,
  trackStreamQuotaExceeded,
  trackStreamError,
  trackStreamCameraDenied,
} from '../telemetry/events'

// INITIAL_METERING is now in ./stream/useStreamMetering (re-exported from there)

const INITIAL_METRICS: StreamMetrics = {
  fps: 0,
  latencyMs: 0,
  frameCount: 0,
  detections: [],
}

/**
 * First-frame watchdog timeout (ms). If a stream enters connecting/buffering and
 * no inference frame arrives within this window, we surface a descriptive error
 * rather than hanging on "Starting inference...". Generous enough to cover a
 * cold GPU + queue wait, tight enough that a truly-stuck (e.g. unauthenticated /
 * unauthorized / no-frame) session fails loudly instead of silently.
 */
const FIRST_FRAME_WATCHDOG_MS = 25000

/** Shown when relay-forced retry also fails — distinct from generic ICE failure */
const ICE_BLOCKED_MSG =
  'Your network is blocking video streaming. Try a different network (Wi-Fi or mobile data), or contact support@primateintelligence.ai.'

/**
 * Server said `terms_not_accepted` — the source-of-truth consent record is
 * missing regardless of any local mirror. Notify useTermsConsent (via window
 * event) so the first-run gate reopens and the user can actually consent.
 * Without this, a stale localStorage flag produced an unrecoverable black
 * "RE-RUN STREAM" dead end (Matt + 2 live testers, prod, 2026-07-01).
 */
function notifyIfTermsRejected(code: string) {
  if (code !== 'terms_not_accepted') return
  try { window.dispatchEvent(new Event('primate:terms-rejected')) } catch { /* SSR/test env */ }
}

/** Narrative message shown when an AUTHENTICATED-but-unauthorized or otherwise
 *  stuck session never produces a first frame. Explains the issue + resolution. */
const FIRST_FRAME_TIMEOUT_MSG_AUTHED =
  'Inference didn\u2019t start \u2014 your session may not be fully authorized. Please refresh the page and sign in again. If you reached this from an invite link, re-open the invite and accept the Terms before trying. If it keeps happening, contact support@primateintelligence.ai.'

/** Narrative message shown when an ANONYMOUS session never produces a first
 *  frame \u2014 most often a not-correctly-authenticated/invite-state issue. */
const FIRST_FRAME_TIMEOUT_MSG_ANON =
  'Inference didn\u2019t start \u2014 you\u2019re not signed in, so this session couldn\u2019t be authorized. Please accept the Terms and try again, or sign in. If you reached this from an invite link, re-open the latest invite email and click \u201cAccept invite\u201d first.'

/**
 * PRI-421 (2026-07-02 prod blank-results incident): a run whose signalling WS
 * never reached the API produced ZERO telemetry — nothing in Sentry, PostHog,
 * or API logs — making the incident undiagnosable after the fact. Every
 * terminal signalling failure now emits a Sentry event + analytics event
 * carrying the connection diagnostics, so the next occurrence tells us whether
 * the WS ever opened, how many retries were burned, and the last close code.
 */
function reportSignalWsFailure(
  diag: SignallingDiagnostics | undefined,
  connectionState: string,
  userPlan: string,
) {
  const errorCode = diag && !diag.everOpened ? 'signal_ws_unreachable' : 'signal_ws_lost'
  try { trackStreamError({ error_code: errorCode, user_plan: userPlan }) } catch { /* non-fatal */ }
  try {
    Sentry.captureMessage(`[stream] signal WS terminal disconnect (${errorCode})`, {
      level: 'error',
      tags: { stream_error_code: errorCode },
      extra: {
        connectionState,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        ...diag,
      },
    })
  } catch { /* non-fatal */ }
}

/** Codec probe: returns true if the browser can natively play the blob's media type. */
function canPlayNatively(blob: Blob): boolean {
  const video = document.createElement('video')
  const type = blob.type || 'video/mp4'
  return video.canPlayType(type) === 'probably'
}

export function useStreamSession(): StreamSessionState & StreamSessionActions {
  // ── Phase 4: State delegated to sub-hooks (PRI-291) ──────────────────────────────
  const { metering, setMetering, meteringRef } = useStreamMetering()
  const {
    narrativeEntry, setNarrativeEntry,
    promptChangeForNarrative, setPromptChangeForNarrative,
    narrativeSummary, setNarrativeSummary,
  } = useStreamNarrative()
  const { recordingBlob, setRecordingBlob, recordingMaxMinutes, setRecordingMaxMinutes } = useStreamRecording()
  const {
    cameraPermission, setCameraPermission,
    devices, setDevices,
    selectedDeviceId, setSelectedDeviceId,
    isFrontCamera, setIsFrontCamera,
    localStream, setLocalStream,
    frozenFrameDataUrl, setFrozenFrameDataUrl,
  } = useCamera()

  // ── Phase 4 (PRI-291): 4 remaining sub-hooks ──────────────────────────────────────
  const { signallingRef, preConnectSignallingRef, signallingCallbacksRef } = useSignalling()
  const { webrtcRef, dataChannelRef, preConnectWebRTCRef, preConnectDataChannelRef, cachedTurnRef } = useWebRTCConnection()
  const {
    isFileStream, setIsFileStream,
    fileDuration, setFileDuration,
    fileCurrentTime, setFileCurrentTime,
    fileVideoRef, fileObjectUrlRef, fileBlobRef, fileTimeUpdateIntervalRef, isFileStreamRef,
  } = useFileStream()
  const { preConnectAbortRef, preConnectSessionIdRef, preConnectRemoteStreamRef } = usePreConnect()

  // ── Remaining state (still in orchestrator) ─────────────────────────────────────
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle')
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  const [queue, setQueue] = useState<StreamQueueState | null>(null)
  const [metrics, setMetrics] = useState<StreamMetrics>(INITIAL_METRICS)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [stopReason, setStopReason] = useState<string | null>(null)
  const [lastStopDuration_s, setLastStopDuration_s] = useState<number | null>(null)
  // PRI-552: terminal settlement summary fetched from GET /v1/streams/{id}
  // after a public-plane session ends. Null mid-session and on the legacy plane.
  const [terminalUsage, setTerminalUsage] = useState<PillUsage | null>(null)
  const [inferenceResult, setInferenceResult] = useState<LiveInferenceResult | null>(null)
  // Sub-step progress for the 'connecting' phase (0-4):
  // 0=starting, 1=TURN fetched, 2=WS connected, 3=auth OK, 4=WebRTC connected
  const [connectProgress, setConnectProgress] = useState<number>(0)
  const [combinedPrompt, setCombinedPrompt] = useState<string | null>(null)
  const [promptUpdateError, setPromptUpdateError] = useState<string | null>(null)

  // ── Orchestrator refs (cross-cutting concerns) ───────────────────────────────────
  const localStreamRef = useRef<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingStreamRef = useRef<MediaStream | null>(null) // cloned stream for recording
  const recordingBlobRef = useRef<Blob | null>(null)
  // Canvas-mirror recording: draw each frame mirrored onto a canvas and record
  // the canvas stream so the saved file is already in webcam (mirror) orientation.
  const mirrorCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const mirrorRafRef = useRef<number | null>(null) // requestAnimationFrame handle
  const mirrorSrcVideoRef = useRef<HTMLVideoElement | null>(null) // hidden video driving the canvas
  const promptRef = useRef<string>('')
  const userPlanRef = useRef<string>('free')
  const promptUpdateErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recalculatingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recalculatingStartRef = useRef<number>(0)
  // Buffer narrative entries that arrive between 'recalculating' and 'prompt_context'.
  // This prevents on_frame metadata entries from appearing before the prompt-change block.
  const pendingPromptContextRef = useRef<boolean>(false)
  const bufferedNarrativeEntriesRef = useRef<import('../lib/narrative').NarrativeEntry[]>([])
  const connectionStateRef = useRef<ConnectionState>('idle')
  const metricsRef = useRef<StreamMetrics>(INITIAL_METRICS)
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // First-frame watchdog: if a session enters connecting/buffering and never
  // reaches 'live' (no inference frame arrives), surface a descriptive error
  // instead of leaving the user stuck on "Starting inference..." forever.
  // (2026-06-11 — investor onboarding regression: an unauthenticated/unauthorized
  // session silently produced no frames and the UI hung.)
  const firstFrameWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Guards max 1 relay retry per user-initiated connect. Reset in startStream. */
  const iceRelayRetryDoneRef = useRef(false)
  /** True while a relay retry is in flight — used to re-arm first-frame watchdog. */
  const iceRelayRetryInProgressRef = useRef(false)
  /** Stores jwtToken from startStream for use in async relay retry callback. */
  const jwtTokenRef = useRef<string | null>(null)
  // PRI-388: true once the first inference metadata frame has arrived for the
  // current session. Used to gate file-stream auto-stop so a SHORT uploaded clip
  // (e.g. a 3s .MOV) does not finish playing and stop the session BEFORE
  // inference has warmed up (~4.5s to first frame) — which produced 0 frames /
  // 0-duration sessions for testers. While false, a file video LOOPS instead of
  // ending the stream.
  const inferenceLiveRef = useRef<boolean>(false)
  // Whether the in-flight session was started with a real JWT (authenticated).
  // Drives the watchdog's auth-aware narrative message.
  const sessionAuthedRef = useRef<boolean>(false)
  // meteringRef is now in ./stream/useStreamMetering (delegated in Phase 4)
  const recordingMaxMinutesRef = useRef<number>(30)
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Timing anchors for frame sync — used to compute recording_elapsed_ms
  const recordingStartedAtRef = useRef<number>(0) // performance.now() when MediaRecorder.start() is called
  const serverAnchorMsRef = useRef<number | null>(null)  // first server_elapsed_ms received
  const browserAnchorMsRef = useRef<number | null>(null)  // performance.now() at that moment
  const useSamRef = useRef<boolean>(false)
  // Guard: prevents camera re-acquisition after an intentional stop/clear.
  // Set to true by cleanup() and onClearVideo. Cleared when user explicitly selects a device.
  const cameraReleasedRef = useRef<boolean>(false)
  // Timing instrumentation: performance.now() timestamps for each pipeline step
  const setupTimingRef = useRef<Record<string, number>>({})

  // Keep refs in sync with state to avoid stale closures in callbacks
  useEffect(() => { connectionStateRef.current = connectionState }, [connectionState])
  useEffect(() => { metricsRef.current = metrics }, [metrics])
  // meteringRef sync useEffect now lives in ./stream/useStreamMetering

  // ── First-frame watchdog ────────────────────────────────────────────────
  // Arms while a live stream is connecting/buffering. If it never reaches
  // 'live' (no inference frame) within FIRST_FRAME_WATCHDOG_MS, transition to
  // 'error' with a descriptive, auth-aware narrative message + resolution steps.
  // This is what turns the silent "Starting inference..." hang (observed for
  // unauthenticated/unauthorized investor sessions on prod) into actionable UX.
  useEffect(() => {
    const clearWatchdog = () => {
      if (firstFrameWatchdogRef.current) {
        clearTimeout(firstFrameWatchdogRef.current)
        firstFrameWatchdogRef.current = null
      }
    }
    if (connectionState === 'connecting' || connectionState === 'buffering') {
      // Re-arm a single timer for the whole connecting→buffering window.
      if (!firstFrameWatchdogRef.current) {
        firstFrameWatchdogRef.current = setTimeout(function fireWatchdog() {
          firstFrameWatchdogRef.current = null
          // If relay retry is still spinning, re-arm for a fresh window
          if (iceRelayRetryInProgressRef.current) {
            firstFrameWatchdogRef.current = setTimeout(fireWatchdog, FIRST_FRAME_WATCHDOG_MS)
            return
          }
          const cur = connectionStateRef.current
          // Only fire if we're STILL waiting for the first frame.
          if (cur === 'connecting' || cur === 'buffering') {
            const msg = sessionAuthedRef.current
              ? FIRST_FRAME_TIMEOUT_MSG_AUTHED
              : FIRST_FRAME_TIMEOUT_MSG_ANON
            console.warn('[Stream] First-frame watchdog fired — no inference frame within %dms (authed=%s). Surfacing error.', FIRST_FRAME_WATCHDOG_MS, sessionAuthedRef.current)
            try { trackStreamError({ error_code: 'first_frame_timeout', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
            setErrorMessage(msg)
            cleanup(undefined, 'first_frame_timeout', 'error')
          }
        }, FIRST_FRAME_WATCHDOG_MS)
      }
    } else {
      // Reached 'live' (or stopped/error/idle) — disarm.
      clearWatchdog()
    }
    return clearWatchdog
    // cleanup is stable (useCallback); intentionally not re-arming on its identity.
  }, [connectionState]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Device enumeration ──────────────────────────────────────────────────

  const enumerateDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      const videoInputs = all.filter(d => d.kind === 'videoinput')
      setDevices(videoInputs)
      if (videoInputs.length === 0) {
        setConnectionState('no_camera')
      }
      return videoInputs
    } catch (err) {
      console.error('[Stream] Device enumeration failed:', err)
      setDevices([])
      setConnectionState('no_camera')
      return []
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check camera permission on mount
  useEffect(() => {
    if (!navigator.mediaDevices) return
    navigator.permissions?.query({ name: 'camera' as PermissionName })
      .then(result => {
        if (result.state === 'granted') {
          setCameraPermission('granted')
          enumerateDevices()
        } else if (result.state === 'denied') {
          setCameraPermission('denied')
          setConnectionState('camera_denied')
        }
        result.onchange = () => {
          if (result.state === 'granted') {
            setCameraPermission('granted')
            enumerateDevices()
          } else if (result.state === 'denied') {
            setCameraPermission('denied')
            setConnectionState('camera_denied')
          }
        }
      })
      .catch(() => {
        // Permissions API not available — enumerate anyway
        enumerateDevices()
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enumerateDevices])

  // ── Camera access ───────────────────────────────────────────────────────

  const requestCameraAccess = useCallback(async () => {
    // Regression fix (Arian 2026-06-22): reuse an already-live camera instead of
    // calling getUserMedia again. cleanup() now keeps the camera warm across a
    // normal stream end, so on the NEXT prompt we already hold a live MediaStream.
    // Re-acquiring would needlessly re-run getUserMedia (camera flicker + on some
    // browsers/devices a fresh device/permission prompt). If the existing stream
    // is still live, keep it and just ensure we're in the idle/ready state.
    const existing = localStreamRef.current
    if (existing) {
      const liveTrack = existing.getVideoTracks().some(t => t.readyState === 'live')
      if (liveTrack && !cameraReleasedRef.current) {
        console.log('[Stream] Reusing already-live camera — skipping getUserMedia')
        setCameraPermission('granted')
        // PRI-414: never clobber a terminal 'error' state — the auto camera
        // re-acquisition used to flip error→idle within ~100ms, wiping the
        // error tray before the user could read it (black RE-RUN card bug).
        // Retry/dismiss call reset() first, which legitimately returns to idle.
        if (connectionStateRef.current !== 'idle' && connectionStateRef.current !== 'error') setConnectionState('idle')
        return
      }
    }
    console.log('[Stream] Requesting camera access')
    try {
      const constraints: MediaStreamConstraints = {
        video: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : { facingMode: { ideal: 'environment' } },
        audio: false,
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      // Guard: if releaseCamera() was called while getUserMedia was in-flight, stop and bail
      if (cameraReleasedRef.current) {
        stream.getTracks().forEach(t => t.stop())
        console.log('[Stream] Camera released while acquiring — discarding stream')
        return
      }
      localStreamRef.current = stream
      setLocalStream(stream)
      setCameraPermission('granted')
      // PRI-414: after a terminal stream error, cleanup() tears the camera down
      // and the re-acquisition effect immediately re-runs getUserMedia. This
      // used to unconditionally setConnectionState('idle'), clobbering 'error'
      // and resetting streamErrorState before the tray could render — the
      // user saw a silent black RE-RUN STREAM card instead of the narrative.
      // Camera recovery states (camera_denied/no_camera) SHOULD flip to idle;
      // terminal 'error' must persist until the user retries or dismisses.
      if (connectionStateRef.current !== 'error') setConnectionState('idle')
      const trackLabel = stream.getVideoTracks()[0]?.label ?? ''
      console.log('[Stream] Camera acquired:', trackLabel)
      Sentry.addBreadcrumb({
        category: 'camera',
        message: 'getUserMedia succeeded',
        level: 'info',
        data: { trackCount: stream.getVideoTracks().length, trackStates: stream.getVideoTracks().map(t => t.readyState), label: trackLabel },
      })
      const allDevices = await enumerateDevices()
      const trackSettings = stream.getVideoTracks()[0]?.getSettings()
      // Detect front/back camera. Prefer the MediaTrackSettings facingMode field;
      // fall back to label heuristics for browsers/devices that omit it.
      const detectedFacing = trackSettings?.facingMode
      const labelLower = trackLabel.toLowerCase()
      const frontByLabel = labelLower.includes('front') || (!labelLower.includes('back') && !labelLower.includes('environment') && !labelLower.includes('wide') && !labelLower.includes('ultra'))
      const isFront = detectedFacing ? detectedFacing === 'user' : frontByLabel
      setIsFrontCamera(isFront)
      console.log('[Stream] isFrontCamera:', isFront, '(facing:', detectedFacing, 'label:', trackLabel, ')')

      // Prefer the wide-angle back camera when no device has been explicitly selected.
      // iOS may default to the telephoto or ultra-wide; look for a device labelled
      // 'Back Camera' (exact, always wide-angle on iOS) or containing 'wide' but not 'ultra'.
      if (!selectedDeviceId) {
        const currentLabel = stream.getVideoTracks()[0]?.label ?? ''
        const isWide = (l: string) =>
          l.toLowerCase() === 'back camera' ||
          (/wide/i.test(l) && !/ultra/i.test(l))
        if (!isWide(currentLabel)) {
          const wideDevice = allDevices.find(d => isWide(d.label))
          if (wideDevice && wideDevice.deviceId !== trackSettings?.deviceId) {
            try {
              stream.getTracks().forEach(t => t.stop())
              const wideStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: wideDevice.deviceId } },
                audio: false,
              })
              // Guard again after wide-camera switch
              if (cameraReleasedRef.current) {
                wideStream.getTracks().forEach(t => t.stop())
                console.log('[Stream] Camera released while acquiring wide — discarding stream')
                return
              }
              localStreamRef.current = wideStream
              setLocalStream(wideStream)
              console.log('[Stream] Switched to wide camera:', wideStream.getVideoTracks()[0]?.label)
              const wideSettings = wideStream.getVideoTracks()[0]?.getSettings()
              if (wideSettings?.deviceId) setSelectedDeviceId(wideSettings.deviceId)
              // Wide camera is always back-facing
              setIsFrontCamera(false)
              return
            } catch {
              // Wide camera unavailable — keep original stream
              localStreamRef.current = stream
              setLocalStream(stream)
            }
          }
        }
      }

      if (trackSettings?.deviceId) {
        setSelectedDeviceId(trackSettings.deviceId)
      }
    } catch (err) {
      const error = err as DOMException
      console.log('[Stream] Camera access denied:', error.name)
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setCameraPermission('denied')
        setConnectionState('camera_denied')
        trackStreamCameraDenied({ user_plan: userPlanRef.current })
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setConnectionState('no_camera')
      } else {
        setConnectionState('error')
        setErrorMessage(`Camera error: ${error.message}`)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId, enumerateDevices])

  // ── Device selection ────────────────────────────────────────────────────

  const selectDevice = useCallback((deviceId: string) => {
    // User explicitly selecting a device — clear the release guard so camera can be acquired.
    cameraReleasedRef.current = false
    setSelectedDeviceId(deviceId)
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Explicitly release the camera and clear the device selection.
  // Use this when the user clicks X on the source chip — stops camera AND prevents re-acquisition.
  const releaseCamera = useCallback(() => {
    cameraReleasedRef.current = true
    setSelectedDeviceId(null)
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-acquire the camera after a file stream or intentional stop.
  // Clears the release guard and re-requests camera access without touching selectedDeviceId.
  // Use this when the user switches back to webcam mode (not a full clear/X action).
  const reacquireCamera = useCallback(() => {
    cameraReleasedRef.current = false
    if (!localStreamRef.current) {
      requestCameraAccess()
    }
  }, [requestCameraAccess])

  // Re-acquire camera when device is selected and we have permission.
  // cameraReleasedRef guards against re-acquisition after intentional stop/clear.
  useEffect(() => {
    if (selectedDeviceId && cameraPermission === 'granted' && !localStream && !cameraReleasedRef.current) {
      requestCameraAccess()
    }
  }, [selectedDeviceId, cameraPermission, localStream, requestCameraAccess])

  // ── PRI-281: cancelPreConnect ──────────────────────────────────
  // Tears down any pre-connect in progress or completed.
  // Safe to call multiple times; no-op if not pre-connecting.
  const cancelPreConnect = useCallback(() => {
    if (preConnectAbortRef.current) {
      preConnectAbortRef.current.abort()
      preConnectAbortRef.current = null
    }
    if (preConnectDataChannelRef.current) {
      try { preConnectDataChannelRef.current.close() } catch { /* ignore */ }
      preConnectDataChannelRef.current = null
    }
    if (preConnectWebRTCRef.current) {
      preConnectWebRTCRef.current.close()
      preConnectWebRTCRef.current = null
    }
    if (preConnectSignallingRef.current) {
      // Send stop before closing so the server can clean up the session cleanly.
      // send() is a no-op if the WS isn't open yet, so this is always safe.
      preConnectSignallingRef.current.send({ type: 'stop' })
      preConnectSignallingRef.current.disconnect()
      preConnectSignallingRef.current = null
    }
    // Note: we intentionally do NOT stop localStreamRef tracks here.
    // localStream is managed by requestCameraAccess/releaseCamera; stopping tracks
    // here would kill the camera preview after the user cancels, breaking retry UX.
    preConnectSessionIdRef.current = null
    // Only reset state if we were actually pre-connecting; don't clobber 'live' etc.
    setConnectionState(prev => (prev === 'pre_connecting' || prev === 'pre_connected') ? 'idle' : prev)
    connectionStateRef.current = connectionStateRef.current === 'pre_connecting' || connectionStateRef.current === 'pre_connected' ? 'idle' : connectionStateRef.current
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── PRI-281: preConnect ────────────────────────────────────────
  // Silently establishes WebRTC before the user hits Submit.
  // - No UI change: pre_connecting/pre_connected are hidden from UI.
  // - On Submit: startStream() detects pre_connected and injects prompt via DC.
  // - cancelPreConnect() must be called when switching to file/offline mode.
  const preConnect = useCallback(async (jwtToken: string | null) => {
    // jwtToken === null → anonymous session. Server accepts empty token.
    // We still warm up TURN + WebRTC so anon users get the same fast-path UX.
    if (jwtToken === undefined) {
      console.log('[PreConnect] Token undefined — skipping')
      return
    }
    // PRI-496 Lane B: pre-connect (connect/set_prompt vocabulary) is a
    // legacy-plane concept — the public plane creates the stream with its
    // prompt at POST /v1/streams time. Skip; startStream takes the normal path.
    if (resolveStreamPlane() === 'public') {
      console.log('[PreConnect] Public streams plane — pre-connect not supported, skipping')
      return
    }
    // 2026-07-01 prod outage: connectNoPrompt sessions could establish WebRTC
    // and send camera frames, but the initial_prompt never reached the sidecar.
    // That left users with a black "RE-RUN STREAM" card and no inference. Keep
    // the experimental fast path available to tests / explicit opt-in only; prod
    // must use the normal prompt-in-session-start path until this is redesigned.
    const preconnectEnabled = import.meta.env.MODE === 'test'
      || import.meta.env.VITE_STREAM_PRECONNECT_ENABLED === '1'
    if (!preconnectEnabled) {
      console.log('[PreConnect] Disabled — using normal prompt-in-offer stream path')
      return
    }
    // Only start if idle AND no pre-connect resources are still live.
    // The second guard prevents a new pre-connect from starting when cancelPreConnect()
    // temporarily reset connectionState to 'idle' while the first session's resources
    // (DC, WebRTC, signalling) are still active and usable by the fast path.
    if (connectionStateRef.current !== 'idle' || preConnectSignallingRef.current) {
      console.log('[PreConnect] Not idle or pre-connect resources still active (state=%s) — skipping', connectionStateRef.current)
      return
    }
    if (!localStreamRef.current) {
      console.log('[PreConnect] No local stream — skipping')
      return
    }

    // Per-instance guard: only THIS pre-connect session forwards to real inference
    // handlers via signallingCallbacksRef. Set to true when pre_connected is reached.
    // Duplicate sessions (stopped:duplicate_session) always call cancelPreConnect.
    let isThisSessionActive = false

    const abort = new AbortController()
    preConnectAbortRef.current = abort

    setConnectionState('pre_connecting')
    connectionStateRef.current = 'pre_connecting'
    console.log('[PreConnect] Starting pre-connect')

    try {
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.primateintelligence.ai'
      const sessionId = crypto.randomUUID()
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      // TURN: use cached if available, else fetch in parallel.
      // PRI-433 P4: TURN credentials require auth server-side now. Anonymous
      // sessions can't stream anyway (WS auth rejects empty tokens), so a 401
      // here just short-circuits to the sign-in path.
      const hasCachedTurn = cachedTurnRef.current && (Date.now() - cachedTurnRef.current.fetchedAt) < 240_000
      const turnPromise: Promise<TurnResult> = hasCachedTurn
        ? Promise.resolve({ servers: cachedTurnRef.current!.servers, failed: false as const })
        : getTurnCredentials(jwtToken, { apiBase }).then(result => {
            if (!result.failed && result.servers) {
              cachedTurnRef.current = { servers: result.servers, fetchedAt: Date.now() }
            } else if (result.failed) {
              console.warn('[Stream] TURN fetch failed in pre-connect — using STUN-only')
              Sentry.addBreadcrumb({ category: 'webrtc', message: 'preConnect TURN failed — STUN-only', level: 'warning' })
              try { trackStreamError({ error_code: 'turn_fetch_failed', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
            }
            return result
          })

      if (abort.signal.aborted) { console.log('[PreConnect] Aborted after TURN'); return }

      const { servers: iceServers } = await turnPromise

      if (abort.signal.aborted) { console.log('[PreConnect] Aborted after TURN resolve'); return }

      // Build WebRTC peer
      let resolvePreConnected!: () => void
      const preConnectedPromise = new Promise<void>(r => { resolvePreConnected = r })

      const webrtc = new WebRTCClient({
        onPeerConnectionReady: (pc: RTCPeerConnection) => {
          const dc = pc.createDataChannel('prompt-control', { ordered: true })
          preConnectDataChannelRef.current = dc
          dc.onopen = () => console.log('[PreConnect] Data channel open')
        },
        onRemoteStream: (stream: MediaStream) => {
          // Pre-connect session: sidecar is connected but NOT inferring yet.
          // Store the remote stream so the fast path can start recording when
          // the real prompt arrives via initial_prompt DC message.
          preConnectRemoteStreamRef.current = stream
          resolvePreConnected()
        },
        onFirstFrame: () => { /* no-op during pre-connect */ },
        onStats: () => { /* no-op */ },
        onDegraded: () => { /* no-op */ },
        onIceCandidate: (candidate: RTCIceCandidateInit) => {
          preConnectSignallingRef.current?.send({ type: 'ice', candidate })
        },
        iceServers,
        onIceConnectionFailed: () => {
          console.log('[PreConnect] ICE failed — resetting to idle')
          cancelPreConnect()
        },
        onIceStuck: (diagnostics) => {
          console.log('[PreConnect] ICE stuck in checking — cancelling (pre-connect is opportunistic)')
          Sentry.addBreadcrumb({
            category: 'webrtc',
            message: 'preConnect ICE stuck',
            level: 'warning',
            data: { candidateCounts: diagnostics.candidateCounts },
          })
          try { trackStreamError({ error_code: 'ice_stuck_checking', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
          cancelPreConnect()
        },
      })
      preConnectWebRTCRef.current = webrtc

      if (abort.signal.aborted) { console.log('[PreConnect] Aborted before signalling'); webrtc.close(); return }

      // Build signalling connection using connectNoPrompt
      const signalling = new SignallingClient({
        url: getStreamSignalUrl(apiBase),
        token: jwtToken,
        onWsOpen: () => {
          if (abort.signal.aborted) signalling.disconnect()
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'preConnect WS open', level: 'info' })
        },
        onAuthOk: () => {
          console.log('[PreConnect] Auth OK')
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'preConnect auth_ok', level: 'info' })
        },
        onQueued: (pos, wait) => {
          console.log(`[PreConnect] Queued at position ${pos}, wait ~${wait}s`)
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'preConnect queued', level: 'info', data: { pos, wait } })
          // Show queue position to the user so they know they're waiting, not stuck.
          setConnectionState('queued')
          connectionStateRef.current = 'queued'
          setQueue({ position: pos, estimated_wait_s: wait })
        },
        onPositionUpdate: (pos, wait) => {
          setQueue({ position: pos, estimated_wait_s: wait })
        },
        onReady: async () => {
          if (abort.signal.aborted) return
          // Slot granted (possibly after waiting in queue) — clear queue state and
          // show that we're now negotiating the WebRTC connection.
          setConnectionState('pre_connecting')
          connectionStateRef.current = 'pre_connecting'
          setQueue(null)

          const stream = localStreamRef.current
          if (!stream) {
            Sentry.captureMessage('[useStreamSession] onReady (preConnect): localStream is null', { level: 'error' })
            setErrorMessage('Camera not ready. Please refresh and grant camera access.')
            cancelPreConnect()
            setConnectionState('error')
            connectionStateRef.current = 'error'
            return
          }
          const videoTracks = stream.getVideoTracks()
          if (videoTracks.length === 0 || !videoTracks.some(t => t.readyState === 'live')) {
            Sentry.captureMessage('[useStreamSession] onReady (preConnect): no live video tracks', {
              level: 'error',
              extra: { trackCount: videoTracks.length, trackStates: videoTracks.map(t => t.readyState) },
            })
            setErrorMessage('Your camera stream has no video. Switch cameras or reload and grant access again.')
            cancelPreConnect()
            setConnectionState('error')
            connectionStateRef.current = 'error'
            return
          }

          try {
            const offer = await webrtc.createOffer(stream)
            Sentry.addBreadcrumb({
              category: 'webrtc',
              message: 'createOffer succeeded (preConnect)',
              level: 'info',
              data: { sdpLength: offer.length, hasVideo: offer.includes('m=video') },
            })
            signalling.send({ type: 'offer', sdp: offer })
          } catch (e) {
            Sentry.captureException(e, { tags: { phase: 'createOffer_preConnect' } })
            setErrorMessage('Couldn\u2019t negotiate the video connection. Reload and try again.')
            cancelPreConnect()
            setConnectionState('error')
            connectionStateRef.current = 'error'
            return
          }
        },
        onAnswer: async (sdp: string) => { await webrtc.handleAnswer(sdp) },
        onIce: async (candidate: RTCIceCandidateInit) => { await webrtc.addIceCandidate(candidate) },
        // Forwarding rules:
        //   isThisSessionActive=true + real handler set  → forward to real inference handler
        //   isThisSessionActive=true + no handler yet    → cancelPreConnect (pre-connect failed before Submit)
        //   isThisSessionActive=false (duplicate session) → NO-OP only.
        //     Duplicate sessions never completed offer/answer so they have no shared resources.
        //     Calling cancelPreConnect() from here was the root bug: it nulled the ACTIVE
        //     session's shared refs, bypassing the fast path and falling to duplicate_session.
        onStreaming: (...args) => { if (isThisSessionActive) signallingCallbacksRef.current.onStreaming?.(...args) },
        onQuotaWarning: (...args) => { if (isThisSessionActive) signallingCallbacksRef.current.onQuotaWarning?.(...args) },
        onSessionEnded: (...args) => {
          if (!isThisSessionActive) return // duplicate session — no-op
          if (signallingCallbacksRef.current.onSessionEnded) signallingCallbacksRef.current.onSessionEnded(...args)
          else cancelPreConnect()
        },
        onQuotaExceeded: () => {
          if (!isThisSessionActive) return
          if (signallingCallbacksRef.current.onQuotaExceeded) signallingCallbacksRef.current.onQuotaExceeded()
          else cancelPreConnect()
        },
        onStopped: (...args) => {
          if (!isThisSessionActive) { console.log('[PreConnect] duplicate session stopped — no-op (not touching shared refs)'); return }
          if (signallingCallbacksRef.current.onStopped) signallingCallbacksRef.current.onStopped(...args)
          else cancelPreConnect() // active session stopped before Submit — pre-connect failed
        },
        onStatus: (...args) => { if (isThisSessionActive) signallingCallbacksRef.current.onStatus?.(...args) },
        onError: (...args) => {
          if (!isThisSessionActive) {
            const [code] = args
            // Infrastructure errors (sidecar offline/not ready): reset pre-connect so
            // the useEffect retries a fresh session. Without this the pre-connect hangs
            // forever — the offer was silently rejected and onRemoteStream never fires.
            // PRI-414: slot_busy / prompt_timeout / sidecar_error on a
            // NOT-YET-ACTIVE pre-connect are also reset-to-idle — the slot is
            // gone server-side (PRI-413 releases it), so keeping the dead
            // pre-connect around would make the next submit fail confusingly.
            // The user hasn't submitted yet, so no visible error is needed;
            // a subsequent submit opens a fresh session and any persistent
            // capacity problem surfaces there with a visible tray.
            if (code === 'no_inference_server' || code === 'not_ready' || code === 'slot_busy' || code === 'prompt_timeout' || code === 'sidecar_error') {
              console.log('[PreConnect] Infrastructure error:', code, '— resetting to idle for retry')
              cancelPreConnect()
              return
            }
            // Terminal auth-gate rejections must NOT be swallowed. Before this
            // guard, a pre-connect (not-yet-active) socket rejected with
            // terms_not_accepted fell through to the no-op below, producing a
            // silent black "RE-RUN STREAM" on the first post-consent submit
            // (PRI consent-firstprompt race, 2026-06-26). Surface it so the
            // error tray renders instead of nothing, and reset to idle so a
            // subsequent (now-consented) submit can pre-connect cleanly.
            if (code === 'terms_not_accepted' || code === 'unauthorized' || code === 'forbidden') {
              console.warn('[PreConnect] Terminal auth error during pre-connect:', code, '— surfacing + resetting')
              notifyIfTermsRejected(code)
              const [, message] = args
              // PRI-414: route the CODE through for known codes so
              // classifyStreamError's exact-code narrative fires (the raw
              // server message used to bypass classification entirely).
              setErrorMessage(
                streamErrorDisplayInput(
                  typeof code === 'string' ? code : null,
                  typeof message === 'string' ? message : null,
                ),
              )
              cancelPreConnect()
              return
            }
            console.log('[PreConnect] duplicate session error (', code, ') — no-op')
            return
          }
          if (signallingCallbacksRef.current.onError) signallingCallbacksRef.current.onError(...args)
          else cancelPreConnect()
        },
        onDisconnected: (diag) => {
          if (!isThisSessionActive) { console.log('[PreConnect] duplicate session disconnected — no-op'); return }
          if (signallingCallbacksRef.current.onDisconnected) signallingCallbacksRef.current.onDisconnected(diag)
          else if (connectionStateRef.current === 'pre_connecting' || connectionStateRef.current === 'pre_connected') {
            // PRI-421: pre-connect died with no fast-path handler attached — report it.
            reportSignalWsFailure(diag, connectionStateRef.current, userPlanRef.current)
            cancelPreConnect()
          }
        },
        onMetadata: (...args) => { if (isThisSessionActive) signallingCallbacksRef.current.onMetadata(...args) },
      })
      preConnectSignallingRef.current = signalling
      preConnectSessionIdRef.current = sessionId

      signalling.connectNoPrompt(sessionId, timezone)

      // Wait for remote stream to arrive (= WebRTC + ICE established)
      await Promise.race([
        preConnectedPromise,
        new Promise<void>((_, rej) => abort.signal.addEventListener('abort', () => rej(new Error('aborted')))),
      ])

      if (abort.signal.aborted) { console.log('[PreConnect] Aborted after ICE'); return }

      // Mark this specific session instance as the active one.
      // From this point, its callbacks will forward to signallingCallbacksRef (real handlers).
      // All other pre-connect sessions (duplicates) continue to call cancelPreConnect only.
      isThisSessionActive = true

      // Transfer refs to normal streaming slots and mark as pre_connected.
      // startStream() will detect this state and skip connection setup.
      setConnectionState('pre_connected')
      connectionStateRef.current = 'pre_connected'
      console.log('[PreConnect] pre_connected — WebRTC ready, waiting for Submit')

    } catch (err) {
      if ((err as Error)?.message === 'aborted') {
        console.log('[PreConnect] Aborted (normal — mode switch or cancel)')
      } else {
        console.warn('[PreConnect] Failed:', err, '— will use normal startStream on Submit')
      }
      // Always clean up on any error path
      if (preConnectAbortRef.current === abort) {
        cancelPreConnect()
      }
    }
  }, [cancelPreConnect]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup ─────────────────────────────────────────────────────────────

  const cleanup = useCallback((durationSec?: number, reason?: string, finalState: ConnectionState = 'stopped') => {
    // keepCamera (regression fix — Arian 2026-06-22): on a NORMAL camera-stream
    // end, leave the live camera MediaStream running so the preview persists and
    // the NEXT prompt reuses it WITHOUT a fresh getUserMedia. Previously every
    // stop stopped the tracks + nulled localStream, so each new prompt re-ran
    // getUserMedia and the user had to re-select the camera / re-grant permission
    // every single time.
    //
    // Self-determining (so we don't thread a flag through ~15 callsites): keep
    // ONLY when this is a live camera stream (not a file stream) AND the stream is
    // ending in a normal terminal state ('stopped'). Error-class terminal states
    // ('error', 'camera_denied', 'no_camera', 'queue_full') tear the camera down
    // so a retry re-acquires from a clean slate. Explicit releaseCamera()
    // (user X-click / switch-to-file) and full unmount also still tear down.
    const keepCamera = finalState === 'stopped' && !isFileStreamRef.current && localStreamRef.current != null
    // PRI-281: cancel any pre-connect (noop if not pre-connecting)
    if (preConnectAbortRef.current) { preConnectAbortRef.current.abort(); preConnectAbortRef.current = null }
    if (preConnectDataChannelRef.current) { try { preConnectDataChannelRef.current.close() } catch { /* ignore */ }; preConnectDataChannelRef.current = null }
    if (preConnectWebRTCRef.current) { preConnectWebRTCRef.current.close(); preConnectWebRTCRef.current = null }
    if (preConnectSignallingRef.current) { preConnectSignallingRef.current.disconnect(); preConnectSignallingRef.current = null }
    preConnectSessionIdRef.current = null
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null }
    if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null }
    if (promptUpdateErrorTimerRef.current) { clearTimeout(promptUpdateErrorTimerRef.current); promptUpdateErrorTimerRef.current = null }
    if (recalculatingTimerRef.current) { clearTimeout(recalculatingTimerRef.current); recalculatingTimerRef.current = null }
    pendingPromptContextRef.current = false
    bufferedNarrativeEntriesRef.current = []
    if (dataChannelRef.current) {
      try { dataChannelRef.current.close() } catch { /* ignore */ }
      dataChannelRef.current = null
    }
    setPromptUpdateError(null)
    // PRI-552: capture the public stream id before disconnect, then fetch the
    // terminal usage block (billed_seconds, metering_rule, frames) once the
    // server finalizes the session. Public plane only; legacy has no usage API.
    const publicStreamId = (signallingRef.current as { streamId?: string | null } | null)?.streamId ?? null
    setTerminalUsage(null)
    if (finalState === 'stopped' && publicStreamId) {
      void (async () => {
        // The server finalizes when the signaling socket closes — retry briefly
        // until usage is populated (settlement is async, typically <2s).
        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            const s = await sdk.streams.retrieve(publicStreamId)
            const usage = (s as { usage?: PillUsage | null }).usage
            if (usage && typeof usage.billed_seconds === 'number') {
              setTerminalUsage(usage)
              return
            }
          } catch { /* non-fatal — usage display is best-effort */ }
          await new Promise(r => setTimeout(r, 1500))
        }
      })()
    }
    signallingRef.current?.disconnect()
    signallingRef.current = null
    webrtcRef.current?.close()
    webrtcRef.current = null
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
    mediaRecorderRef.current = null
    // Stop mirror-canvas RAF loop and src video
    if (mirrorRafRef.current != null) { cancelAnimationFrame(mirrorRafRef.current); mirrorRafRef.current = null }
    if (mirrorSrcVideoRef.current) { mirrorSrcVideoRef.current.srcObject = null; mirrorSrcVideoRef.current = null }
    mirrorCanvasRef.current = null
    // Stop canvas-capture stream tracks to free resources
    recordingStreamRef.current?.getTracks().forEach(t => t.stop())
    recordingStreamRef.current = null
    // Capture a frozen frame from the webcam BEFORE stopping tracks (async fire-and-forget).
    // This becomes the replay-ready thumbnail. File streams use a different capture path.
    if (localStreamRef.current && !isFileStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0]
      if (track) {
        try {
          const cap = new (window as unknown as { ImageCapture: new (t: MediaStreamTrack) => { grabFrame(): Promise<ImageBitmap> } }).ImageCapture(track)
          cap.grabFrame().then((bm: ImageBitmap) => {
            const cv = document.createElement('canvas')
            cv.width = bm.width; cv.height = bm.height
            const ctx = cv.getContext('2d')
            if (ctx) { ctx.translate(bm.width, 0); ctx.scale(-1, 1); ctx.drawImage(bm, 0, 0) }
            setFrozenFrameDataUrl(cv.toDataURL('image/jpeg', 0.8))
          }).catch(() => {})
        } catch { /* ImageCapture unavailable (e.g. Firefox) */ }
      }
    }
    // Stop the live camera tracks. Do NOT set the release guard here — that is only
    // for explicit user X-clicks (releaseCamera()).
    //
    // Regression fix (Arian 2026-06-22): for a NORMAL camera stream end we now KEEP
    // the live camera MediaStream alive (keepCamera=true) so the preview persists
    // and the next prompt reuses it. Previously every stream stop stopped the tracks
    // + nulled localStream, so the next submit re-ran getUserMedia and the user had
    // to re-select the camera/grant permission on every single prompt. File streams
    // (isFileStreamRef) and explicit releaseCamera()/full unmount still tear down.
    if (keepCamera && !isFileStreamRef.current && localStreamRef.current) {
      // Leave localStreamRef + setLocalStream intact — camera stays warm.
    } else {
      localStreamRef.current?.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }
    // Reset timing anchors for next session
    serverAnchorMsRef.current = null
    browserAnchorMsRef.current = null
    recordingStartedAtRef.current = 0
    setNarrativeEntry(null)
    setPromptChangeForNarrative(null)
    setNarrativeSummary(null)
    // Clean up file stream resources
    if (fileTimeUpdateIntervalRef.current) {
      clearInterval(fileTimeUpdateIntervalRef.current)
      fileTimeUpdateIntervalRef.current = null
    }
    if (fileVideoRef.current) {
      // Clean up canvas intermediary if used (Safari)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const canvas = (fileVideoRef.current as any)._primatCanvas as HTMLCanvasElement | undefined
      if (canvas) canvas.remove()
      fileVideoRef.current.pause()
      fileVideoRef.current.srcObject = null
      fileVideoRef.current.src = ''
      fileVideoRef.current.remove()
      fileVideoRef.current = null
    }
    if (fileObjectUrlRef.current) {
      URL.revokeObjectURL(fileObjectUrlRef.current)
      fileObjectUrlRef.current = null
    }
    fileBlobRef.current = null
    isFileStreamRef.current = false
    setIsFileStream(false)
    setFileDuration(0)
    setFileCurrentTime(0)
    setRemoteStream(null)
    setQueue(null)
    setInferenceResult(null)
    if (durationSec !== undefined) {
      setLastStopDuration_s(durationSec)
      setStopReason(reason ?? null)
      // Use refs to avoid stale closure
      trackStreamStopped({
        duration_s: durationSec,
        stop_reason: reason ?? 'unknown',
        user_plan: userPlanRef.current,
        avg_fps: metricsRef.current.fps,
        avg_latency_ms: metricsRef.current.latencyMs,
      })
    }
    // Log timing summary once per session (only if t_submit was recorded)
    const t = setupTimingRef.current
    if (t.t_submit !== undefined) {
      console.log('[Stream Timing]', JSON.stringify({
        total_to_first_frame_ms: t.t_first_frame !== undefined ? Math.round(t.t_first_frame - t.t_submit) : null,
        total_to_first_narrative_ms: t.t_first_narrative !== undefined ? Math.round(t.t_first_narrative - t.t_submit) : null,
        camera_ms: t.t_camera_ready !== undefined ? Math.round(t.t_camera_ready - t.t_submit) : null,
        imports_ms: t.t_modules_loaded !== undefined && t.t_camera_ready !== undefined ? Math.round(t.t_modules_loaded - t.t_camera_ready) : null,
        turn_ms: t.t_turn_fetched !== undefined && t.t_modules_loaded !== undefined ? Math.round(t.t_turn_fetched - t.t_modules_loaded) : null,
        ws_connect_ms: t.t_ws_connected !== undefined && t.t_turn_fetched !== undefined ? Math.round(t.t_ws_connected - t.t_turn_fetched) : null,
        auth_ms: t.t_auth_ok !== undefined && t.t_ws_connected !== undefined ? Math.round(t.t_auth_ok - t.t_ws_connected) : null,
        webrtc_to_stream_ms: t.t_remote_stream !== undefined && t.t_auth_ok !== undefined ? Math.round(t.t_remote_stream - t.t_auth_ok) : null,
        stream_to_first_frame_ms: t.t_first_frame !== undefined && t.t_remote_stream !== undefined ? Math.round(t.t_first_frame - t.t_remote_stream) : null,
        first_frame_to_narrative_ms: t.t_first_narrative !== undefined && t.t_first_frame !== undefined ? Math.round(t.t_first_narrative - t.t_first_frame) : null,
      }))
      setupTimingRef.current = {}
    }

    setConnectionState(finalState)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // stable — reads from refs only

  // ── Stream start ────────────────────────────────────────────────────────

  const startStream = useCallback(async (
    prompt: string,
    jwtToken: string | null,
    userPlan: string,
    useSam: boolean = false,
  ) => {
    setupTimingRef.current = { t_submit: performance.now() }
    useSamRef.current = useSam ?? false
    // Record whether this session is authenticated (real JWT) vs anonymous so the
    // first-frame watchdog can show the right resolution guidance if it fires.
    sessionAuthedRef.current = !!jwtToken
    setFrozenFrameDataUrl(null) // clear previous session's frozen frame

    // ── PRI-281: pre-connect fast path ──────────────────────────────────────
    // WebRTC already established. Inject prompt via DC only.
    // NOTE: don't check connectionStateRef — a duplicate-session cancelPreConnect()
    // may have temporarily reset it to 'idle' while resources are still valid.
    // We rely on the pre-connect resources being present instead.
    if (preConnectDataChannelRef.current &&
        preConnectSignallingRef.current &&
        preConnectWebRTCRef.current) {
      console.log('[Stream] PRI-281: pre-connect resources available — injecting prompt via DC (fast path)')
      promptRef.current = prompt
      userPlanRef.current = userPlan

      // Transfer pre-connect resources to the normal streaming refs FIRST.
      signallingRef.current = preConnectSignallingRef.current
      webrtcRef.current = preConnectWebRTCRef.current
      dataChannelRef.current = preConnectDataChannelRef.current
      preConnectSignallingRef.current = null
      preConnectWebRTCRef.current = null
      preConnectDataChannelRef.current = null

      // ── Bug fix: update state BEFORE any await so cancelPreConnect() cannot reset
      // to 'idle' during the async settings fetch (which triggered duplicate pre-connects).
      setConnectionState('buffering')
      connectionStateRef.current = 'buffering'
      preConnectAbortRef.current = null  // prevent cancelPreConnect abort during await
      preConnectSessionIdRef.current = null
      setConnectProgress(4)
      setErrorMessage(null)
      setMetrics(INITIAL_METRICS)
      setMetering(INITIAL_METERING)
      setQueue(null)

      // ── Bug fix: wire real inference callbacks onto the transferred SignallingClient.
      // Pre-connect created this client with forwarding stubs via signallingCallbacksRef.
      // Populate the ref with real handlers now so metadata/streaming messages are handled.
      // All callbacks use hook-level refs/setters only — no duplication of closure state.
      signallingCallbacksRef.current = {
        onStreaming: (elapsed: number, sessionRemaining: number, dailyRemaining: number) => {
          setMetering({ elapsed_s: elapsed, session_remaining_s: sessionRemaining, daily_remaining_s: dailyRemaining, daily_used_s: elapsed })
          const cur = connectionStateRef.current
          if (cur !== 'live' && cur !== 'degraded' && cur !== 'quota_warning' && cur !== 'recalculating') setConnectionState('live')
        },
        onQuotaWarning: (sessionRemaining: number, dailyRemaining: number) => {
          setConnectionState('quota_warning')
          setMetering(prev => ({ ...prev, session_remaining_s: sessionRemaining, daily_remaining_s: dailyRemaining }))
          trackStreamQuotaWarning({ remaining_s: sessionRemaining, user_plan: userPlanRef.current })
        },
        onSessionEnded: (durationSec: number, dailyRemaining: number) => {
          setMetering(prev => ({ ...prev, elapsed_s: durationSec, daily_remaining_s: dailyRemaining }))
          setConnectionState('stopping')
          cleanup(durationSec, 'session_limit')
        },
        onQuotaExceeded: () => { trackStreamQuotaExceeded({ user_plan: userPlanRef.current }); cleanup(meteringRef.current.elapsed_s, 'daily_quota') },
        onStopped: (durationSec: number, reason: string) => {
          console.log(`[PRI-281 fast-path] onStopped reason=${reason} duration=${durationSec} state=${connectionStateRef.current}`)
          // PRI-424: a server_error stop is the API declaring the stream dead
          // server-side (e.g. sidecar crash). Surface an error tray instead of
          // ending as a silent normal stop.
          if (reason === 'server_error') {
            try { trackStreamError({ error_code: 'server_error', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
            setErrorMessage(streamErrorDisplayInput('server_error'))
            cleanup(durationSec, reason, 'error')
            return
          }
          cleanup(durationSec, reason)
        },
        onStatus: (status: string, message: string) => {
          if (status === 'error') {
            setErrorMessage(message || 'Inference stopped unexpectedly. Please retry.')
            cleanup(undefined, undefined, 'error')
            return
          }
          if (status === 'recalculating') {
            if (recalculatingTimerRef.current) clearTimeout(recalculatingTimerRef.current)
            recalculatingStartRef.current = Date.now(); setConnectionState('recalculating')
            pendingPromptContextRef.current = true; bufferedNarrativeEntriesRef.current = []
          } else if (status === 'prompt_update_ok') {
            if (pendingPromptContextRef.current) { pendingPromptContextRef.current = false; const o = bufferedNarrativeEntriesRef.current; bufferedNarrativeEntriesRef.current = []; o.forEach(e => setNarrativeEntry(e)) }
            if (connectionStateRef.current === 'recalculating') {
              const elapsed = Date.now() - recalculatingStartRef.current; const rem = Math.max(0, 1500 - elapsed)
              if (recalculatingTimerRef.current) clearTimeout(recalculatingTimerRef.current)
              recalculatingTimerRef.current = setTimeout(() => { setConnectionState('live'); recalculatingTimerRef.current = null }, rem)
            }
          } else if (status === 'prompt_context') {
            try {
              const ctx = JSON.parse(message) as { combined: string; entries: Array<{ text: string; entry_type: string; wall_clock: string; elapsed_s: number }> }
              const now = new Date(); const wallClock = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
              const elapsedS = (Date.now() - (recalculatingStartRef.current || Date.now())) / 1000
              const buffered = bufferedNarrativeEntriesRef.current; pendingPromptContextRef.current = false; bufferedNarrativeEntriesRef.current = []
              // The server echoes back the wire-format prompt (embedCompiledQuery output)
              // verbatim as ctx.combined. Strip the envelope here so the state never
              // holds the |||COMPILED_QUERY:<base64>||| suffix that would otherwise leak
              // into pill.prompt and surface in the result-pill header.
              const cleanCombined = ctx.combined ? stripCompiledQueryEnvelope(ctx.combined) : ctx.combined
              if (cleanCombined) setCombinedPrompt(cleanCombined)
              setPromptChangeForNarrative({ prompt: cleanCombined, wallClock, elapsedS, contextEntries: ctx.entries, bufferedEntries: buffered })
            } catch { /* ignore malformed */ }
          } else if (status === 'narrative_entry') {
            if (!pendingPromptContextRef.current) { try { const entry = JSON.parse(message) as { text: string; entry_type: string; wall_clock: string; elapsed_s: number }; setNarrativeEntry({ wall_clock: entry.wall_clock, elapsed_s: entry.elapsed_s, window_start_elapsed_s: entry.elapsed_s, text: entry.text, type: (entry.entry_type ?? 'prompt_change') as import('../lib/narrative').NarrativeEntry['type'] }) } catch { /* ignore */ } }
          } else if (status === 'combined_prompt') {
            // Server may echo wire-format with the compiled-query envelope. Strip it.
            if (message) setCombinedPrompt(stripCompiledQueryEnvelope(message))
          } else if (status === 'prompt_update_failed') {
            if (recalculatingTimerRef.current) clearTimeout(recalculatingTimerRef.current)
            if (connectionStateRef.current === 'recalculating') setConnectionState('live')
            setPromptUpdateError('Failed to switch prompt — try again')
            if (promptUpdateErrorTimerRef.current) clearTimeout(promptUpdateErrorTimerRef.current)
            promptUpdateErrorTimerRef.current = setTimeout(() => { setPromptUpdateError(null); promptUpdateErrorTimerRef.current = null }, 4000)
          }
        },
        onError: (code: string, message: string) => {
          console.log(`[PRI-281 fast-path] onError code=${code} message=${message} state=${connectionStateRef.current}`)
          trackStreamError({ error_code: code, user_plan: userPlanRef.current })
          notifyIfTermsRejected(code)
          if (code === 'queue_full') cleanup(undefined, undefined, 'queue_full')
          // PRI-414: route known codes through so classifyStreamError's
          // exact-code narratives fire (slot_busy/sidecar_error/prompt_timeout
          // etc.) instead of showing the raw server prose — or nothing.
          else { setErrorMessage(streamErrorDisplayInput(code, message)); cleanup(undefined, undefined, 'error') }
        },
        onDisconnected: (diag) => {
          console.log(`[PRI-281 fast-path] onDisconnected state=${connectionStateRef.current}`, diag)
          const cur = connectionStateRef.current; if (cur === 'stopping') return
          const setupStates = ['connecting', 'queued', 'buffering', 'pre_connecting', 'pre_connected']
          if (cur === 'live' || cur === 'degraded' || setupStates.includes(cur)) {
            // PRI-421: a run that dies here previously left ZERO telemetry (the
            // 2026-07-02 prod blank-results incident was invisible in Sentry,
            // PostHog and API logs). Report it before painting the error UI.
            reportSignalWsFailure(diag, cur, userPlanRef.current)
            setErrorMessage(diag && !diag.everOpened
              ? 'Couldn\u2019t reach the streaming service. Check your connection and try again.'
              : 'Lost connection to the inference service. Check your network and retry.')
            cleanup(undefined, undefined, 'error')
            return
          }
          cleanup()
        },

        onMetadata: (_frameNum: number, inference: LiveInferenceResult) => {
          const cur = connectionStateRef.current
          // PRI-388: mark inference live so a short file clip can stop naturally.
          inferenceLiveRef.current = true
          if (cur === 'buffering' || cur === 'connecting') {
            console.log('[Stream] First inference metadata received — transitioning to live')
            setupTimingRef.current.t_first_frame = performance.now()
            setConnectionState('live'); trackStreamLive({ user_plan: userPlanRef.current })
          }
          if (serverAnchorMsRef.current === null && inference.server_elapsed_ms != null) {
            serverAnchorMsRef.current = inference.server_elapsed_ms; browserAnchorMsRef.current = performance.now()
          }
          let recordingElapsedMs: number | undefined
          if (serverAnchorMsRef.current != null && browserAnchorMsRef.current != null && recordingStartedAtRef.current > 0 && inference.server_elapsed_ms != null) {
            const serverDeltaMs = inference.server_elapsed_ms - serverAnchorMsRef.current
            const offsetFromRecordingStart = browserAnchorMsRef.current - recordingStartedAtRef.current
            recordingElapsedMs = Math.round(serverDeltaMs + offsetFromRecordingStart)
          }
          const augmented: LiveInferenceResult = { ...inference, recording_elapsed_ms: recordingElapsedMs }
          setInferenceResult(augmented)
          if (inference.narrative_update) {
            if (setupTimingRef.current.t_first_narrative === undefined) setupTimingRef.current.t_first_narrative = performance.now()
            const entry = inference.narrative_update as import('../lib/narrative').NarrativeEntry
            let narrativeRecordingElapsedMs: number | undefined
            if (serverAnchorMsRef.current != null && browserAnchorMsRef.current != null && recordingStartedAtRef.current > 0) {
              const serverDeltaMs = (entry.window_start_elapsed_s * 1000) - serverAnchorMsRef.current
              narrativeRecordingElapsedMs = Math.round(serverDeltaMs + (browserAnchorMsRef.current - recordingStartedAtRef.current))
            }
            const narrativeWithTiming: import('../lib/narrative').NarrativeEntry = { ...entry, recording_elapsed_ms: narrativeRecordingElapsedMs }
            if (pendingPromptContextRef.current) { bufferedNarrativeEntriesRef.current = [narrativeWithTiming, ...bufferedNarrativeEntriesRef.current] } else { setNarrativeEntry(narrativeWithTiming) }
          }
          if (inference.narrative_summary?.all_entries) setNarrativeSummary(inference.narrative_summary.all_entries)
          if (inference.answer !== undefined || inference.confidence !== undefined || inference.narrative_update !== undefined) {
            setMetrics(prev => ({ ...prev, detections: [{ label: `${inference.answer ?? '—'} · ${inference.search_term ?? ''}`.trim(), confidence: inference.confidence ?? 0, bbox: [0,0,0,0] as [number,number,number,number] }] }))
          }
        },
      }

      // Tell Railway the real prompt (for DB logging).
      // Anonymous sessions also need to send their prompt so the API can route
      // it to the inference server and start producing narrative output.
      ;(signallingRef.current as SignallingClient).setPromptViaWS(prompt)

      // Set remote stream so the video slot shows the inference feed.
      const preConnectStream = preConnectRemoteStreamRef.current
      if (preConnectStream) {
        setRemoteStream(preConnectStream)
        // Start canvas recording (webcam pre-connect is always mirrored).
        const shouldMirror = isFrontCamera // mirror only for front/selfie camera
        try {
          const srcVideo = document.createElement('video')
          // When SAM is off, record from local source (30fps) instead of remote
          const recordingSource = useSamRef.current
            ? preConnectStream.clone()
            : localStreamRef.current ?? preConnectStream.clone()
          srcVideo.srcObject = recordingSource
          srcVideo.muted = true; srcVideo.playsInline = true
          srcVideo.play().catch(() => {})
          mirrorSrcVideoRef.current = srcVideo
          const canvas = document.createElement('canvas')
          canvas.width = 1280; canvas.height = 720
          mirrorCanvasRef.current = canvas
          const ctx = canvas.getContext('2d')!
          const drawFrame = () => {
            if (!mirrorSrcVideoRef.current || mirrorSrcVideoRef.current.readyState < 2) { mirrorRafRef.current = requestAnimationFrame(drawFrame); return }
            const vw = mirrorSrcVideoRef.current.videoWidth; const vh = mirrorSrcVideoRef.current.videoHeight
            if (vw > 0 && vh > 0) {
              if (canvas.width !== vw || canvas.height !== vh) { canvas.width = vw; canvas.height = vh }
              if (shouldMirror) { ctx.save(); ctx.translate(vw, 0); ctx.scale(-1, 1); ctx.drawImage(mirrorSrcVideoRef.current, 0, 0, vw, vh); ctx.restore() }
              else ctx.drawImage(mirrorSrcVideoRef.current, 0, 0, vw, vh)
            }
            mirrorRafRef.current = requestAnimationFrame(drawFrame)
          }
          mirrorRafRef.current = requestAnimationFrame(drawFrame)
          const recordingStream = canvas.captureStream(30)
          recordingStreamRef.current = recordingStream
          const mimeType = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1') ? 'video/mp4;codecs=avc1' : MediaRecorder.isTypeSupported('video/webm;codecs=h264') ? 'video/webm;codecs=h264' : MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm'
          const recorder = new MediaRecorder(recordingStream, { mimeType, videoBitsPerSecond: 4_000_000 })
          const chunks: Blob[] = []
          recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
          recorder.onstop = () => {
            // File blob optimization: for non-SAM file streams, prefer original file
            if (isFileStreamRef.current && !useSamRef.current && fileBlobRef.current && canPlayNatively(fileBlobRef.current)) {
              console.log('[Stream] Using original file blob as recording (non-SAM optimization)')
              recordingBlobRef.current = fileBlobRef.current
              setRecordingBlob(fileBlobRef.current)
            } else {
              const blob = new Blob(chunks, { type: mimeType })
              recordingBlobRef.current = blob
              setRecordingBlob(blob)
            }
          }
          recorder.start()
          recordingStartedAtRef.current = performance.now()
          mediaRecorderRef.current = recorder
          const tid = setTimeout(() => { if (mediaRecorderRef.current?.state === 'recording') { mediaRecorderRef.current.stop(); console.log('[Stream] Recording stopped at max duration limit') } }, recordingMaxMinutesRef.current * 60 * 1000)
          recordingTimeoutRef.current = tid
        } catch (recErr) { console.warn('[Stream] MediaRecorder not available:', recErr) }
      }

      // Build promptWithConfig — always send PRIMATE_CFG so sam_enabled is respected.
      // Settings fetch enriches with server config (narrative etc) but is optional.
      let promptWithConfig = prompt
      try {
        const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.primateintelligence.ai'
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const cfg: Record<string, unknown> = {
          narrative_enabled: true,
          threshold: 0.5,
          narrative_stride_frames: 16,
          plan_budget: userPlanRef.current === 'free' ? 50 : userPlanRef.current === 'pro' ? 150 : 300,
          timezone,
          sam_enabled: useSam,  // always explicit — server defaults True without this
          video_enabled: useSam,  // when SAM is off, server skips video frame encoding
        }
        try {
          // PRI-388: public read-only config endpoint (was /v1/admin/settings,
          // which 403'd for non-admin testers and dumped a scary console 403).
          const settingsData = await getStreamConfig({ apiBase })
          if (Object.keys(settingsData).length > 0) {
            cfg.narrative_enabled = settingsData.narrative_enabled === true || settingsData.narrative_enabled === 1 || settingsData.narrative_enabled === 'true'
            cfg.threshold = (Number(settingsData.narrative_threshold) || 50) / 100
            cfg.narrative_stride_frames = Number(settingsData.narrative_stride_frames) || 16
          }
        } catch { /* settings fetch non-fatal — use defaults */ }
        promptWithConfig = `${prompt}|||PRIMATE_CFG:${btoa(JSON.stringify(cfg))}`
      } catch { /* non-fatal */ }

      const dc = dataChannelRef.current
      if (dc) {
        let initialPromptSent = false
        const sendInitialPrompt = (fromOpenEvent = false) => {
          if (initialPromptSent || (!fromOpenEvent && dc.readyState !== 'open')) return
          dc.send(JSON.stringify({ type: 'initial_prompt', prompt: promptWithConfig }))
          initialPromptSent = true
          console.log('[Stream] PRI-281: initial_prompt sent via DC')
        }
        if (dc.readyState === 'open') sendInitialPrompt()
        else {
          const onOpen = () => sendInitialPrompt(true)
          dc.addEventListener?.('open', onOpen, { once: true })
          dc.onopen = onOpen
          window.setTimeout(() => {
            sendInitialPrompt()
            if (initialPromptSent || dc.readyState === 'closed') return
            console.warn('[Stream] PRI-281: initial_prompt NOT sent — data channel state=', dc.readyState)
            trackStreamError({ error_code: 'initial_prompt_not_sent', user_plan: userPlanRef.current })
            setErrorMessage('Stream connected, but the prompt could not be sent. Please retry.')
            cleanup(undefined, undefined, 'error')
          }, 5000)
        }
      }

      preConnectRemoteStreamRef.current = null
      return // Fast path complete.
    }
    // ───────────────────────────────────────────────────────────

    // If a pre-connect is in-flight but hasn't established the data channel yet
    // (e.g. it got queued while the slot was taken), cancel it now. This disconnects
    // the pre-connect WS which causes Railway to remove that queue entry, so the slow
    // path can join fresh without hitting a duplicate_session error.
    if (preConnectSignallingRef.current && !preConnectDataChannelRef.current) {
      console.log('[Stream] Cancelling queued pre-connect before slow-path join')
      cancelPreConnect()
    }

    if (!localStreamRef.current) {
      await requestCameraAccess()
      if (!localStreamRef.current) return
    }
    setupTimingRef.current.t_camera_ready = performance.now()
    promptRef.current = prompt
    userPlanRef.current = userPlan

    const track = localStreamRef.current.getVideoTracks()[0]
    const settings = track?.getSettings()
    const resolution = settings ? `${settings.width ?? 0}x${settings.height ?? 0}` : 'unknown'
    const cameraLabel = track?.label ?? 'unknown'

    console.log('[Stream] Starting stream, prompt:', prompt)
    setConnectionState('connecting')
    setConnectProgress(0)
    setErrorMessage(null)
    setMetrics(INITIAL_METRICS)
    setMetering(INITIAL_METERING)
    setQueue(null)
    setRemoteStream(null)

    trackStreamStarted({ prompt, user_plan: userPlan, camera_label: cameraLabel, resolution })

    try {
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.primateintelligence.ai'

      // PRI-496 Lane B: resolve which streaming plane this session rides.
      // 'public' = POST /v1/streams + versioned signal WS (default);
      // 'legacy' = internal /v1/stream/signal (instant kill-switch rollback).
      const streamPlane = resolveStreamPlane()
      console.log('[Stream] Streaming plane:', streamPlane)

      // OPTIMIZATION: Use pre-warmed TURN if available (< 4 min old), otherwise
      // start TURN fetch in parallel with dynamic imports.
      // Public plane: ice_servers arrive on the POST /v1/streams response — no
      // internal turn-credentials fetch (onIceServers wires them in below).
      const hasCachedTurn = cachedTurnRef.current && (Date.now() - cachedTurnRef.current.fetchedAt) < 240_000
      const turnPromiseOuter: Promise<TurnResult> = streamPlane === 'public'
        ? Promise.resolve({ servers: undefined, failed: false as const })
        : hasCachedTurn
        ? Promise.resolve({ servers: cachedTurnRef.current!.servers, failed: false as const })
        : getTurnCredentials(jwtToken, { apiBase }).then(result => {
            if (!result.failed && result.servers) {
              cachedTurnRef.current = { servers: result.servers, fetchedAt: Date.now() }
              console.log('[Stream] TURN credentials fetched, servers:', result.servers.length)
            } else if (result.failed) {
              console.warn('[Stream] TURN credential fetch failed — falling back to STUN only')
              Sentry.addBreadcrumb({ category: 'webrtc', message: 'TURN fetch failed — STUN-only fallback', level: 'warning' })
              try { trackStreamError({ error_code: 'turn_fetch_failed', user_plan: userPlan }) } catch { /* non-fatal */ }
            }
            return result
          })
      if (hasCachedTurn && streamPlane === 'legacy') console.log('[Stream] Using pre-warmed TURN credentials')

      // Static imports — modules are always bundled now (Phase 2 is complete).
      // This eliminates ~100ms of dynamic import overhead on each session start.
      setupTimingRef.current.t_modules_loaded = performance.now()

      const sessionId = crypto.randomUUID()
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      // Resolve TURN credentials (started in parallel with imports above)
      const { servers: iceServers } = await turnPromiseOuter
      // Reset relay retry guard for each new user-initiated connect
      iceRelayRetryDoneRef.current = false
      iceRelayRetryInProgressRef.current = false
      jwtTokenRef.current = jwtToken
      setupTimingRef.current.t_turn_fetched = performance.now()
      setConnectProgress(1) // TURN fetched

      const webrtc = new WebRTCClient({
        onPeerConnectionReady: (pc: RTCPeerConnection) => {
          // Create data channel for prompt updates BEFORE the offer
          const dc = pc.createDataChannel('prompt-control', { ordered: true })
          dataChannelRef.current = dc
          dc.onopen = () => { console.log('[PRI-263] data channel OPEN, readyState=', dc.readyState) }
          dc.onclose = () => { console.log('[PRI-263] data channel CLOSED'); dataChannelRef.current = null }
          dc.onerror = (e) => { console.warn('[PRI-263] data channel ERROR', e); dataChannelRef.current = null }
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'peer connection ready', level: 'info' })
          pc.addEventListener?.('iceconnectionstatechange', () => {
            Sentry.addBreadcrumb({ category: 'webrtc', message: `ICE state: ${pc.iceConnectionState}`, level: 'info' })
          })
        },
        onRemoteStream: (stream: MediaStream) => {
          console.log('[Stream] WebRTC connected, first frame received')
          setupTimingRef.current.t_remote_stream = performance.now()
          setConnectProgress(4) // WebRTC connected
          setRemoteStream(stream)
          setConnectionState('buffering')
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'remote stream received', level: 'info' })
          // Record using a canvas pipeline. For webcam streams, mirror horizontally
          // so the saved file matches the selfie-view display. For file streams,
          // record without mirroring (file video is already correct orientation).
          const shouldMirror = !isFileStreamRef.current && isFrontCamera // back camera is never mirrored
          try {
            // 1. Hidden video element to drive the canvas — plays the remote stream.
            const srcVideo = document.createElement('video')
            // When SAM is off, record from local source (30fps) instead of remote (inference fps)
            const recordingSource = useSamRef.current
              ? stream.clone()
              : localStreamRef.current ?? stream.clone()  // fallback to remote if local somehow missing
            srcVideo.srcObject = recordingSource
            srcVideo.muted = true
            srcVideo.playsInline = true
            srcVideo.play().catch(() => {})
            mirrorSrcVideoRef.current = srcVideo

            // 2. Off-screen canvas — dimensions set on first valid frame to avoid
            //    codec dimension-change corruption (was causing glitchy recordings).
            const canvas = document.createElement('canvas')
            canvas.width = 1280  // placeholder; resized before recorder starts
            canvas.height = 720
            mirrorCanvasRef.current = canvas
            const ctx = canvas.getContext('2d')!

            // Memoize codec selection once
            const mimeType = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')
              ? 'video/mp4;codecs=avc1'
              : MediaRecorder.isTypeSupported('video/webm;codecs=h264')
              ? 'video/webm;codecs=h264'
              : MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
              ? 'video/webm;codecs=vp9'
              : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
              ? 'video/webm;codecs=vp8'
              : 'video/webm'
            console.log('[Stream] Mirror-canvas recording codec:', mimeType)

            let recorderStarted = false
            const chunks: Blob[] = []

            const startRecorder = () => {
              if (recorderStarted) return
              recorderStarted = true
              const recordingStream = canvas.captureStream(30)
              recordingStreamRef.current = recordingStream
              const recorder = new MediaRecorder(recordingStream, {
                mimeType,
                videoBitsPerSecond: 4_000_000,
              })
              recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
              recorder.onstop = () => {
                // File blob optimization: for non-SAM file streams, prefer original file
                if (isFileStreamRef.current && !useSamRef.current && fileBlobRef.current && canPlayNatively(fileBlobRef.current)) {
                  console.log('[Stream] Using original file blob as recording (non-SAM optimization)')
                  recordingBlobRef.current = fileBlobRef.current
                  setRecordingBlob(fileBlobRef.current)
                } else {
                  const blob = new Blob(chunks, { type: mimeType })
                  recordingBlobRef.current = blob
                  setRecordingBlob(blob)
                }
              }
              recorder.start()
              recordingStartedAtRef.current = performance.now()
              mediaRecorderRef.current = recorder
              console.log(`[Stream] Recorder started at ${canvas.width}x${canvas.height}`)
              const recordingTimeoutId = setTimeout(() => {
                if (mediaRecorderRef.current?.state === 'recording') {
                  mediaRecorderRef.current.stop()
                  console.log('[Stream] Recording stopped at max duration limit')
                }
              }, recordingMaxMinutesRef.current * 60 * 1000)
              recordingTimeoutRef.current = recordingTimeoutId
            }

            // 3. RAF loop: draw each frame, mirroring only for webcam.
            //    Recorder starts only after canvas is correctly sized (prevents
            //    mid-stream dimension change which causes glitchy encoded video).
            const drawFrame = () => {
              if (!mirrorSrcVideoRef.current || mirrorSrcVideoRef.current.readyState < 2) {
                mirrorRafRef.current = requestAnimationFrame(drawFrame)
                return
              }
              const vw = mirrorSrcVideoRef.current.videoWidth
              const vh = mirrorSrcVideoRef.current.videoHeight
              if (vw > 0 && vh > 0) {
                // Resize canvas to match actual video dimensions before first draw
                if (canvas.width !== vw || canvas.height !== vh) {
                  canvas.width = vw
                  canvas.height = vh
                }
                if (shouldMirror) {
                  ctx.save()
                  ctx.translate(vw, 0)
                  ctx.scale(-1, 1)
                  ctx.drawImage(mirrorSrcVideoRef.current, 0, 0, vw, vh)
                  ctx.restore()
                } else {
                  ctx.drawImage(mirrorSrcVideoRef.current, 0, 0, vw, vh)
                }
                // Start recorder only after canvas has correct dimensions
                startRecorder()
              }
              mirrorRafRef.current = requestAnimationFrame(drawFrame)
            }
            mirrorRafRef.current = requestAnimationFrame(drawFrame)
          } catch (recErr) {
            console.warn('[Stream] MediaRecorder not available:', recErr)
          }
        },
        onFirstFrame: () => {
          // Track timing but do NOT transition to 'live' here.
          // onFirstFrame fires immediately with onRemoteStream (same ontrack event),
          // so 'buffering' would never be visible. Instead, stay in 'buffering' until
          // the first onMetadata or onStreaming message arrives — that proves inference
          // is actually running and producing results.
          setupTimingRef.current.t_first_frame = performance.now()
          console.log('[Stream] onFirstFrame: WebRTC track arrived, staying in buffering until inference output')
        },
        onStats: (stats: { fps: number; latencyMs: number }) => {
          setMetrics(prev => ({
            ...prev,
            fps: stats.fps,
            latencyMs: stats.latencyMs,
          }))
          console.log(`[Stream] FPS: ${stats.fps}, latency: ${stats.latencyMs}ms`)
        },
        onDegraded: () => {
          setConnectionState('degraded')
          // When video plays locally at 30fps but inference is degraded, explain in narrative
          if (!useSamRef.current) {
            setNarrativeEntry({
              text: 'Inference performance is degraded — results may update more slowly.',
              type: 'status' as const,
              wall_clock: new Date().toLocaleTimeString(),
              elapsed_s: meteringRef.current.elapsed_s,
              window_start_elapsed_s: meteringRef.current.elapsed_s,
            })
          }
        },
        onIceCandidate: (candidate: RTCIceCandidateInit) => {
          signalling.send({ type: 'ice', candidate })
        },
        iceServers,
        onIceConnectionFailed: () => {
          setConnectionState('error')
          setErrorMessage('WebRTC connection failed. Your network may block video streaming.')
          cleanup()
        },
        onIceStuck: (diagnostics) => {
          Sentry.addBreadcrumb({
            category: 'webrtc',
            message: 'ICE stuck in checking — relay retry',
            level: 'warning',
            data: { candidateCounts: diagnostics.candidateCounts, stateHistoryLength: diagnostics.stateHistory.length },
          })
          try { trackStreamError({ error_code: 'ice_stuck_checking', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }

          if (iceRelayRetryDoneRef.current || streamPlane === 'public') {
            // Already retried with relay — surface network-blocked error.
            // Public plane: the create response already carried TURN relay
            // candidates (resolveIceServers server-side), so a relay-only
            // retry adds nothing — and the legacy relay-retry flow below
            // rides internal endpoints. Surface the network-blocked error.
            try { trackStreamError({ error_code: 'ice_relay_retry_failed', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
            setErrorMessage(ICE_BLOCKED_MSG)
            cleanup(undefined, 'ice_relay_retry_failed', 'error')
            return
          }
          iceRelayRetryDoneRef.current = true
          iceRelayRetryInProgressRef.current = true

          // Close current session — don't surface error yet
          webrtcRef.current?.close()
          webrtcRef.current = null
          signallingRef.current?.disconnect()
          signallingRef.current = null

          // Clear the first-frame watchdog — iceRelayRetryInProgressRef=true causes
          // the watchdog useEffect to re-arm for a fresh FIRST_FRAME_WATCHDOG_MS window.
          if (firstFrameWatchdogRef.current) {
            clearTimeout(firstFrameWatchdogRef.current)
            firstFrameWatchdogRef.current = null
          }

          void (async () => {
            // Fetch fresh TURN credentials (relay requires actual TURN, not STUN)
            const relayTurnResult = await getTurnCredentials(jwtTokenRef.current, { apiBase })
            if (relayTurnResult.failed || !relayTurnResult.servers) {
              iceRelayRetryInProgressRef.current = false
              try { trackStreamError({ error_code: 'ice_relay_retry_failed', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
              setErrorMessage(ICE_BLOCKED_MSG)
              cleanup(undefined, 'ice_relay_retry_failed', 'error')
              return
            }

            const relaySessionId = crypto.randomUUID()
            const relayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

            Sentry.addBreadcrumb({ category: 'webrtc', message: 'ICE relay retry: starting relay session', level: 'info', data: { relaySessionId } })
            try { trackStreamError({ error_code: 'ice_relay_retry_started', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }

            // Create relay WebRTCClient FIRST so signalling's onReady can reference it
            const relaySignallingHolder: { current: SignallingClient | null } = { current: null }

            const relayWebrtc = new WebRTCClient({
              onPeerConnectionReady: (pc: RTCPeerConnection) => {
                const dc = pc.createDataChannel('prompt-control', { ordered: true })
                dataChannelRef.current = dc
                dc.onopen = () => { console.log('[PRI-263] relay retry data channel OPEN') }
                dc.onclose = () => { dataChannelRef.current = null }
                dc.onerror = () => { dataChannelRef.current = null }
                Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry peer connection ready', level: 'info' })
                pc.addEventListener?.('iceconnectionstatechange', () => {
                  Sentry.addBreadcrumb({ category: 'webrtc', message: `relay ICE state: ${pc.iceConnectionState}`, level: 'info' })
                })
              },
              onRemoteStream: (stream: MediaStream) => {
                console.log('[Stream] Relay retry: WebRTC connected')
                setConnectProgress(4)
                setRemoteStream(stream)
                setConnectionState('buffering')
                iceRelayRetryInProgressRef.current = false
                Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry: remote stream received', level: 'info' })
              },
              onFirstFrame: () => {
                setupTimingRef.current.t_first_frame = performance.now()
              },
              onStats: (stats) => {
                setMetrics(prev => ({ ...prev, fps: stats.fps, latencyMs: stats.latencyMs }))
              },
              onDegraded: () => { setConnectionState('degraded') },
              onIceCandidate: (candidate: RTCIceCandidateInit) => {
                relaySignallingHolder.current?.send({ type: 'ice', candidate })
              },
              iceServers: relayTurnResult.servers,
              iceTransportPolicy: 'relay',
              onIceConnectionFailed: () => {
                iceRelayRetryInProgressRef.current = false
                try { trackStreamError({ error_code: 'ice_relay_retry_failed', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
                Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry: ICE connection failed', level: 'error' })
                setErrorMessage(ICE_BLOCKED_MSG)
                cleanup(undefined, 'ice_relay_retry_failed', 'error')
              },
              onIceStuck: (diag) => {
                iceRelayRetryInProgressRef.current = false
                Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry: ICE stuck again', level: 'error', data: { candidateCounts: diag.candidateCounts } })
                try { trackStreamError({ error_code: 'ice_relay_retry_failed', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
                setErrorMessage(ICE_BLOCKED_MSG)
                cleanup(undefined, 'ice_relay_retry_failed', 'error')
              },
            })
            webrtcRef.current = relayWebrtc

            const relaySignalling = new SignallingClient({
              url: getStreamSignalUrl(apiBase),
              token: jwtTokenRef.current,
              onWsOpen: () => { Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry WS open', level: 'info' }) },
              onAuthOk: () => { Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry auth_ok', level: 'info' }) },
              onQueued: (pos, waitSec) => { setConnectionState('queued'); setQueue({ position: pos, estimated_wait_s: waitSec }) },
              onPositionUpdate: (pos, waitSec) => { setQueue({ position: pos, estimated_wait_s: waitSec }) },
              onReady: async () => {
                Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry: ready — creating offer', level: 'info' })
                const stream = localStreamRef.current
                if (!stream) { cleanup(undefined, undefined, 'error'); return }
                try {
                  const offer = await relayWebrtc.createOffer(stream)
                  relaySignalling.send({ type: 'offer', sdp: offer })
                } catch (e) {
                  Sentry.captureException(e, { tags: { phase: 'createOffer_relayRetry' } })
                  cleanup(undefined, undefined, 'error')
                }
              },
              onAnswer: async (sdp) => { await relayWebrtc.handleAnswer(sdp) },
              onIce: async (c) => { await relayWebrtc.addIceCandidate(c) },
              onStreaming: (elapsed, sessionRemaining, dailyRemaining) => {
                setMetering({ elapsed_s: elapsed, session_remaining_s: sessionRemaining, daily_remaining_s: dailyRemaining, daily_used_s: elapsed })
                if (connectionStateRef.current !== 'live' && connectionStateRef.current !== 'degraded') {
                  setConnectionState('live')
                  try { trackStreamError({ error_code: 'ice_relay_retry_success', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
                  Sentry.addBreadcrumb({ category: 'webrtc', message: 'relay retry: stream went live', level: 'info' })
                }
              },
              onQuotaWarning: (sessionRemaining, dailyRemaining) => {
                setConnectionState('quota_warning')
                setMetering(prev => ({ ...prev, session_remaining_s: sessionRemaining, daily_remaining_s: dailyRemaining }))
              },
              onSessionEnded: (durationSec, dailyRemaining) => {
                setMetering(prev => ({ ...prev, elapsed_s: durationSec, daily_remaining_s: dailyRemaining }))
                cleanup(durationSec, 'session_limit')
              },
              onQuotaExceeded: () => { cleanup(meteringRef.current.elapsed_s, 'daily_quota') },
              onStopped: (durationSec, reason) => { cleanup(durationSec, reason) },
              onError: (code, message) => {
                trackStreamError({ error_code: code, user_plan: userPlanRef.current })
                setErrorMessage(streamErrorDisplayInput(code, message))
                cleanup(undefined, undefined, 'error')
              },
              onDisconnected: (diag) => {
                if (connectionStateRef.current === 'stopping') return
                reportSignalWsFailure(diag, connectionStateRef.current, userPlanRef.current)
                setErrorMessage(diag && !diag.everOpened
                  ? 'Couldn\u2019t reach the streaming service. Check your connection and try again.'
                  : 'Lost connection to the inference service. Check your network and retry.')
                cleanup(undefined, undefined, 'error')
              },
              onMetadata: (_frameNum, inference) => {
                if (connectionStateRef.current === 'buffering' || connectionStateRef.current === 'connecting') {
                  setConnectionState('live')
                  try { trackStreamError({ error_code: 'ice_relay_retry_success', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
                }
                setInferenceResult(inference as LiveInferenceResult)
              },
            })
            relaySignallingHolder.current = relaySignalling
            signallingRef.current = relaySignalling

            relaySignalling.connect(relaySessionId, promptRef.current, relayTimezone)
          })()
        },
      })
      webrtcRef.current = webrtc

      const signallingOptions: SignallingClientOptions = {
        url: getStreamSignalUrl(),
        token: jwtToken,
        onWsOpen: () => {
          setupTimingRef.current.t_ws_connected = performance.now()
          setConnectProgress(2)
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'stream WS open', level: 'info' })
        },
        onAuthOk: () => {
          setupTimingRef.current.t_auth_ok = performance.now()
          setConnectProgress(3)
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'stream auth_ok', level: 'info' })
        },
        onQueued: (pos: number, waitSec: number) => {
          console.log(`[Stream] Queued at position ${pos}`)
          setConnectionState('queued')
          setQueue({ position: pos, estimated_wait_s: waitSec })
          trackStreamQueued({ position: pos, user_plan: userPlan })
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'stream queued', level: 'info', data: { pos, waitSec } })
        },
        onPositionUpdate: (pos: number, waitSec: number) => {
          setQueue({ position: pos, estimated_wait_s: waitSec })
        },
        onReady: async (recordingMaxMins: number | null | undefined) => {
          recordingMaxMinutesRef.current = recordingMaxMins ?? 30
          setRecordingMaxMinutes(recordingMaxMinutesRef.current)
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'stream ready signal received', level: 'info' })

          const stream = localStreamRef.current
          if (!stream) {
            Sentry.captureMessage('[useStreamSession] onReady (startStream): localStream is null', { level: 'error' })
            setErrorMessage('Camera not ready. Please refresh and grant camera access.')
            cleanup(undefined, undefined, 'error')
            return
          }
          const videoTracks = stream.getVideoTracks()
          if (videoTracks.length === 0 || !videoTracks.some(t => t.readyState === 'live')) {
            Sentry.captureMessage('[useStreamSession] onReady (startStream): no live video tracks', {
              level: 'error',
              extra: { trackCount: videoTracks.length, trackStates: videoTracks.map(t => t.readyState) },
            })
            setErrorMessage('Your camera stream has no video. Switch cameras or reload and grant access again.')
            cleanup(undefined, undefined, 'error')
            return
          }

          try {
            const offer = await webrtc.createOffer(stream)
            Sentry.addBreadcrumb({
              category: 'webrtc',
              message: 'createOffer succeeded (startStream)',
              level: 'info',
              data: { sdpLength: offer.length, hasVideo: offer.includes('m=video') },
            })
            signalling.send({ type: 'offer', sdp: offer })
          } catch (e) {
            Sentry.captureException(e, { tags: { phase: 'createOffer_startStream' } })
            setErrorMessage('Couldn\u2019t negotiate the video connection. Reload and try again.')
            cleanup(undefined, undefined, 'error')
            return
          }
        },
        onAnswer: async (sdp: string) => {
          Sentry.addBreadcrumb({ category: 'webrtc', message: 'stream answer received', level: 'info' })
          await webrtc.handleAnswer(sdp)
        },
        onIce: async (candidate: RTCIceCandidateInit) => {
          await webrtc.addIceCandidate(candidate)
        },
        onStreaming: (elapsed: number, sessionRemaining: number, dailyRemaining: number) => {
          setMetering({
            elapsed_s: elapsed,
            session_remaining_s: sessionRemaining,
            daily_remaining_s: dailyRemaining,
            daily_used_s: elapsed,
          })
          const cur = connectionStateRef.current
          if (cur !== 'live' && cur !== 'degraded' && cur !== 'quota_warning' && cur !== 'recalculating') {
            setConnectionState('live')
            Sentry.addBreadcrumb({ category: 'webrtc', message: 'stream went live', level: 'info' })
          }
        },
        onQuotaWarning: (sessionRemaining: number, dailyRemaining: number) => {
          console.log(`[Stream] Quota warning: ${sessionRemaining}s remaining`)
          setConnectionState('quota_warning')
          setMetering(prev => ({ ...prev, session_remaining_s: sessionRemaining, daily_remaining_s: dailyRemaining }))
          trackStreamQuotaWarning({ remaining_s: sessionRemaining, user_plan: userPlan })
        },
        onSessionEnded: (durationSec: number, dailyRemaining: number) => {
          setMetering(prev => ({ ...prev, elapsed_s: durationSec, daily_remaining_s: dailyRemaining }))
          setConnectionState('stopping')
          cleanup(durationSec, 'session_limit')
        },
        onQuotaExceeded: () => {
          trackStreamQuotaExceeded({ user_plan: userPlan })
          cleanup(meteringRef.current.elapsed_s, 'daily_quota')
        },
        onStopped: (durationSec: number, reason: string) => {
          console.log(`[Stream] Stopped: ${reason}, duration: ${durationSec}s`)
          // PRI-424: surface mid-stream server-side death as an error, not a
          // silent normal stop (see fast-path handler above).
          if (reason === 'server_error') {
            try { trackStreamError({ error_code: 'server_error', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
            setErrorMessage(streamErrorDisplayInput('server_error'))
            cleanup(durationSec, reason, 'error')
            return
          }
          cleanup(durationSec, reason)
        },
        onStatus: (status: string, message: string) => {
          if (status === 'error') {
            setErrorMessage(message || 'Inference stopped unexpectedly. Please retry.')
            cleanup(undefined, undefined, 'error')
            return
          }
          if (status === 'recalculating') {
            // Clear any pending recalculating timer
            if (recalculatingTimerRef.current) clearTimeout(recalculatingTimerRef.current)
            recalculatingStartRef.current = Date.now()
            setConnectionState('recalculating')
            // Buffer any narrative entries that arrive before prompt_context so they
            // don't appear below the prompt-change block in the narrative log.
            pendingPromptContextRef.current = true
            bufferedNarrativeEntriesRef.current = []
          } else if (status === 'prompt_update_ok') {
            // Safety net: if prompt_context never arrived, clear buffer and flush any
            // buffered entries directly so they're not lost.
            if (pendingPromptContextRef.current) {
              pendingPromptContextRef.current = false
              const orphaned = bufferedNarrativeEntriesRef.current
              bufferedNarrativeEntriesRef.current = []
              orphaned.forEach(e => setNarrativeEntry(e))
            }
            // Restore to live state, but enforce a minimum 1.5s recalculating display time
            const cur = connectionStateRef.current
            if (cur === 'recalculating') {
              const elapsed = Date.now() - recalculatingStartRef.current
              const remaining = Math.max(0, 1500 - elapsed)
              if (recalculatingTimerRef.current) clearTimeout(recalculatingTimerRef.current)
              recalculatingTimerRef.current = setTimeout(() => {
                setConnectionState('live')
                recalculatingTimerRef.current = null
              }, remaining)
            }
          } else if (status === 'prompt_context') {
            // Atomic batch: combined prompt string + unrecognized-action entries.
            // Clears the pending buffer so any on_frame entries that raced ahead
            // are flushed AFTER the prompt-change block (they appear above it, newer).
            try {
              const ctx = JSON.parse(message) as {
                combined: string
                entries: Array<{ text: string; entry_type: string; wall_clock: string; elapsed_s: number }>
              }
              const now = new Date()
              const wallClock = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
              const elapsedS = (Date.now() - (recalculatingStartRef.current || Date.now())) / 1000
              // Drain the buffer — entries that arrived during recalculating will be
              // appended after the prompt-change block via promptChangeForNarrative.
              const buffered = bufferedNarrativeEntriesRef.current
              pendingPromptContextRef.current = false
              bufferedNarrativeEntriesRef.current = []
              // Update pill header with combined prompt.
              // The server echoes back the wire-format prompt (embedCompiledQuery output)
              // verbatim as ctx.combined. Strip the |||COMPILED_QUERY:<base64>||| envelope
              // here so the state never holds it and it can't leak into pill.prompt or the
              // result-pill header. Defense-in-depth: V2ResultPill also strips at render.
              const cleanCombined = ctx.combined ? stripCompiledQueryEnvelope(ctx.combined) : ctx.combined
              if (cleanCombined) setCombinedPrompt(cleanCombined)
              // Inject prompt-change block + buffered entries atomically.
              // PrimateVisionV2 useEffect handles the actual narrative injection.
              setPromptChangeForNarrative({
                prompt: cleanCombined,
                wallClock,
                elapsedS,
                contextEntries: ctx.entries,
                bufferedEntries: buffered,
              })
            } catch { /* ignore malformed */ }
          } else if (status === 'narrative_entry') {
            // Session-start substitution entries arrive via this path (before 'ok/ready').
            // During prompt updates these are now bundled in prompt_context instead,
            // but session-start still uses this channel.
            // Only inject if NOT pending a prompt_context (avoid duplicates).
            if (!pendingPromptContextRef.current) {
              try {
                const entry = JSON.parse(message) as {
                  text: string; entry_type: string; wall_clock: string; elapsed_s: number
                }
                const narrativeEntry: import('../lib/narrative').NarrativeEntry = {
                  wall_clock: entry.wall_clock,
                  elapsed_s: entry.elapsed_s,
                  window_start_elapsed_s: entry.elapsed_s,
                  text: entry.text,
                  type: (entry.entry_type ?? 'prompt_change') as import('../lib/narrative').NarrativeEntry['type'],
                }
                setNarrativeEntry(narrativeEntry)
              } catch { /* ignore malformed */ }
            }
          } else if (status === 'combined_prompt') {
            // Server may echo wire-format with the compiled-query envelope. Strip it.
            if (message) setCombinedPrompt(stripCompiledQueryEnvelope(message))
          } else if (status === 'prompt_update_failed') {
            // Revert to live, show transient error
            if (recalculatingTimerRef.current) clearTimeout(recalculatingTimerRef.current)
            const cur = connectionStateRef.current
            if (cur === 'recalculating') {
              setConnectionState('live')
            }
            setPromptUpdateError('Failed to switch prompt — try again')
            if (promptUpdateErrorTimerRef.current) clearTimeout(promptUpdateErrorTimerRef.current)
            promptUpdateErrorTimerRef.current = setTimeout(() => {
              setPromptUpdateError(null)
              promptUpdateErrorTimerRef.current = null
            }, 4000)
          }
        },
        onError: (code: string, message: string) => {
          console.log('[Stream] Server error:', code, message)
          trackStreamError({ error_code: code, user_plan: userPlan })
          notifyIfTermsRejected(code)
          if (code === 'queue_full') {
            cleanup(undefined, undefined, 'queue_full')
          } else {
            // PRI-414: route known codes through classifyStreamError (see
            // streamErrorDisplayInput) — raw server prose no longer bypasses
            // the friendly-narrative mapping.
            setErrorMessage(streamErrorDisplayInput(code, message))
            cleanup(undefined, undefined, 'error')
          }
        },
        onDisconnected: (diag) => {
          const cur = connectionStateRef.current
          // 'stopping' = user clicked stop — server closes WS normally, not an error
          if (cur === 'stopping') return
          // Surface an error for all active + setup states (not just live/degraded).
          // Setup-phase disconnects (connecting/queued/buffering/pre_connecting/pre_connected)
          // previously fell through to bare cleanup() → blank pill, no message (DB-4).
          const setupStates = ['connecting', 'queued', 'buffering', 'pre_connecting', 'pre_connected']
          if (cur === 'live' || cur === 'degraded' || setupStates.includes(cur)) {
            // PRI-421: report before painting the error UI (see fast-path note).
            reportSignalWsFailure(diag, cur, userPlan)
            setErrorMessage(diag && !diag.everOpened
              ? 'Couldn\u2019t reach the streaming service. Check your connection and try again.'
              : 'Lost connection to the inference service. Check your network and retry.')
            cleanup(undefined, undefined, 'error')
            return
          }
          cleanup()
        },
        onMetadata: (_frameNum: number, inference: LiveInferenceResult) => {
          // Transition to 'live' on first metadata — this proves inference is running
          // and the server is sending annotated frames.
          const cur = connectionStateRef.current
          if (cur === 'buffering' || cur === 'connecting') {
            console.log('[Stream] First inference metadata received — transitioning to live')
            setupTimingRef.current.t_first_frame = performance.now()
            setConnectionState('live')
            trackStreamLive({ user_plan: userPlan })
          }

          // On first metadata, establish server→browser time mapping
          if (serverAnchorMsRef.current === null && inference.server_elapsed_ms != null) {
            serverAnchorMsRef.current = inference.server_elapsed_ms
            browserAnchorMsRef.current = performance.now()
          }

          // Compute recording_elapsed_ms for this frame
          let recordingElapsedMs: number | undefined
          if (
            serverAnchorMsRef.current != null &&
            browserAnchorMsRef.current != null &&
            recordingStartedAtRef.current > 0 &&
            inference.server_elapsed_ms != null
          ) {
            // Time since first metadata on server clock
            const serverDeltaMs = inference.server_elapsed_ms - serverAnchorMsRef.current
            // Offset from recording start to first metadata on browser clock
            const offsetFromRecordingStart = browserAnchorMsRef.current - recordingStartedAtRef.current
            // Use server delta (more accurate for frame spacing) + browser offset from recording start
            recordingElapsedMs = Math.round(serverDeltaMs + offsetFromRecordingStart)
          }

          // Augment the inference result with recording_elapsed_ms
          const augmented: LiveInferenceResult = { ...inference, recording_elapsed_ms: recordingElapsedMs }
          setInferenceResult(augmented)

          // Handle narrative_update — compute recording_elapsed_ms using same anchor
          if (inference.narrative_update) {
            if (setupTimingRef.current.t_first_narrative === undefined) {
              setupTimingRef.current.t_first_narrative = performance.now()
            }
            const entry = inference.narrative_update as NarrativeEntry
            let narrativeRecordingElapsedMs: number | undefined
            if (
              serverAnchorMsRef.current != null &&
              browserAnchorMsRef.current != null &&
              recordingStartedAtRef.current > 0
            ) {
              const serverDeltaMs = (entry.window_start_elapsed_s * 1000) - serverAnchorMsRef.current
              const offsetFromRecordingStart = browserAnchorMsRef.current - recordingStartedAtRef.current
              narrativeRecordingElapsedMs = Math.round(serverDeltaMs + offsetFromRecordingStart)
            }
            const narrativeWithTiming: NarrativeEntry = {
              ...entry,
              recording_elapsed_ms: narrativeRecordingElapsedMs,
            }
            // Buffer entries that arrive while waiting for prompt_context,
            // so they don't appear below the prompt-change block.
            if (pendingPromptContextRef.current) {
              bufferedNarrativeEntriesRef.current = [narrativeWithTiming, ...bufferedNarrativeEntriesRef.current]
            } else {
              setNarrativeEntry(narrativeWithTiming)
            }
          }

          // Handle narrative_summary — final canonical narrative at session end
          if (inference.narrative_summary?.all_entries) {
            setNarrativeSummary(inference.narrative_summary.all_entries)
          }

          // Also update detections list so existing UI panel shows the result
          if (inference.answer !== undefined || inference.confidence !== undefined || inference.narrative_update !== undefined) {
            setMetrics(prev => ({
              ...prev,
              detections: [{
                label: `${inference.answer ?? '—'} · ${inference.search_term ?? ''}`.trim(),
                confidence: inference.confidence ?? 0,
                bbox: [0, 0, 0, 0] as [number, number, number, number],
              }],
            }))
          }
        },
      }

      // PRI-496 Lane B: plane selection. The public adapter creates the stream
      // (POST /v1/streams — prompt compiled + PRIMATE_CFG built server-side),
      // adopts its ice_servers, and speaks the public signal-WS vocabulary
      // through the same legacy callback surface built above.
      const signalling: ISignallingClient = streamPlane === 'public'
        ? new PublicStreamSignalling({
            ...signallingOptions,
            onIceServers: (servers) => {
              webrtc.setIceServers(servers)
              setupTimingRef.current.t_turn_fetched = performance.now()
              setConnectProgress(1)
              console.log('[Stream] Public-plane ICE servers adopted:', servers.length)
            },
          })
        : new SignallingClient(signallingOptions)
      signallingRef.current = signalling

      // Append narrative config — always send PRIMATE_CFG so sam_enabled is respected.
      // Settings fetch enriches with server config but is optional (anon users can't access it).
      // PRI-496 Lane B: public plane builds PRIMATE_CFG server-side
      // (buildInferencePrompt) — the client-side envelope + the internal
      // /v1/stream/config fetch are legacy-only.
      let promptWithConfig = prompt
      if (streamPlane === 'legacy') try {
        const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.primateintelligence.ai'
        const cfg: Record<string, unknown> = {
          narrative_enabled: true,
          threshold: 0.5,
          narrative_stride_frames: 16,
          plan_budget: userPlan === 'free' ? 50 : userPlan === 'pro' ? 150 : 300,
          timezone: timezone,
          sam_enabled: useSam,  // always explicit — server defaults True without this
          video_enabled: useSam,  // when SAM is off, server skips video frame encoding
        }
        try {
          // PRI-388: public read-only config endpoint (see above).
          const settingsData = await getStreamConfig({ apiBase })
          if (Object.keys(settingsData).length > 0) {
            cfg.narrative_enabled = settingsData.narrative_enabled === true || settingsData.narrative_enabled === 1 || settingsData.narrative_enabled === 'true'
            cfg.threshold = (Number(settingsData.narrative_threshold) || 50) / 100
            cfg.narrative_stride_frames = Number(settingsData.narrative_stride_frames) || 16
          }
        } catch { /* settings fetch non-fatal */ }
        const cfgB64 = btoa(JSON.stringify(cfg))
        promptWithConfig = `${prompt}|||PRIMATE_CFG:${cfgB64}`
        console.log('[Stream] Config attached, sam_enabled:', cfg.sam_enabled, 'narrative_enabled:', cfg.narrative_enabled)
      } catch (cfgErr) {
        console.warn('[Stream] Failed to build stream config:', cfgErr)
      }

      signalling.connect(sessionId, streamPlane === 'public' ? prompt : promptWithConfig, timezone)

    } catch (importErr) {
      console.log('[Stream] Signalling/WebRTC modules not available yet', importErr)
      setConnectionState('error')
      setErrorMessage('Streaming server not available yet. Check back soon.')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCameraAccess, cleanup])

  // ── File stream start (stream from video file via captureStream) ───────

  const sendPromptUpdate = useCallback((prompt: string) => {
    const dc = dataChannelRef.current
    console.log('[PRI-263] sendPromptUpdate called, prompt:', prompt, '| dc:', dc ? `readyState=${dc.readyState}` : 'NULL')
    if (dc && dc.readyState === 'open') {
      const msg = JSON.stringify({ type: 'prompt_update', prompt })
      console.log('[PRI-263] sending via data channel:', msg)
      dc.send(msg)
      promptRef.current = prompt
    } else {
      console.warn('[PRI-263] sendPromptUpdate DROPPED — dc not open. dc=', dc, 'readyState=', dc?.readyState)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopStream = useCallback(() => {
    console.log('[Stream] User stopped stream')
    // Set ref immediately (sync) so onDisconnected sees 'stopping' before the
    // useEffect that syncs connectionStateRef can fire (which is async)
    connectionStateRef.current = 'stopping'
    setConnectionState('stopping')
    signallingRef.current?.send({ type: 'stop' })
    stopTimerRef.current = setTimeout(() => {
      if (signallingRef.current) {
        cleanup(meteringRef.current.elapsed_s, 'user_stop')
      }
    }, 3000)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanup])

  const startFileStream = useCallback(async (
    file: File | Blob,
    prompt: string,
    jwtToken: string | null,
    userPlan: string,
    useSam: boolean = false,
  ) => {
    // Guard: prevent double-invocation (e.g. two rapid submit clicks or stale closure re-fire)
    if (isFileStreamRef.current) {
      console.log('[Stream] startFileStream: already in progress, ignoring duplicate call')
      return
    }
    // If a pre-connect is in flight, cancel it first — file streams don't use the pre-connect
    // fast path and must start a fresh WebRTC session.
    const curState = connectionStateRef.current
    if (curState === 'pre_connecting' || curState === 'pre_connected') {
      console.log('[Stream] startFileStream: cancelling pre-connect to allow file stream (state=%s)', curState)
      cancelPreConnect() // synchronously resets connectionStateRef to 'idle'
    } else if (curState !== 'idle') {
      console.log('[Stream] startFileStream: stream already active (state=%s), ignoring', curState)
      return
    }
    console.log('[Stream] Starting file stream, prompt:', prompt, 'file:', file instanceof File ? file.name : 'blob')
    useSamRef.current = useSam ?? false
    isFileStreamRef.current = true
    fileBlobRef.current = file
    setIsFileStream(true)
    setFileDuration(0)
    setFileCurrentTime(0)

    // Create hidden video element to play the file
    const video = document.createElement('video')
    video.style.display = 'none'
    video.muted = true // Required for autoplay
    video.playsInline = true
    // PRI-388: loop the source during warmup so a SHORT clip (e.g. 3s .MOV)
    // keeps feeding frames until inference goes live (~4.5s to first frame).
    // Without this, a clip shorter than the warmup ended -> session stopped at
    // 0 frames / 0 duration. onended below stops the stream only after the
    // first inference result has arrived (inferenceLiveRef) and a full natural
    // playthrough completes.
    video.loop = true
    inferenceLiveRef.current = false
    document.body.appendChild(video)
    fileVideoRef.current = video

    const objectUrl = URL.createObjectURL(file)
    fileObjectUrlRef.current = objectUrl
    video.src = objectUrl

    // Wait for video metadata to load
    try {
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve()
        video.onerror = () => reject(new Error('Failed to load video file'))
        setTimeout(() => reject(new Error('Video load timeout')), 10000)
      })
    } catch (err) {
      console.error('[Stream] Failed to load video:', err)
      setConnectionState('error')
      setErrorMessage('Failed to load video file.')
      setIsFileStream(false)
      isFileStreamRef.current = false
      return
    }

    setFileDuration(video.duration)

    // Start playing (required before capturing frames)
    try {
      await video.play()
    } catch (playErr) {
      console.error('[Stream] Failed to play video:', playErr)
      setConnectionState('error')
      setErrorMessage('Failed to play video file.')
      setIsFileStream(false)
      isFileStreamRef.current = false
      return
    }

    // Get MediaStream from video.
    // Safari does NOT support video.captureStream() — use a canvas intermediary.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const directCapture = (video as any).captureStream || (video as any).mozCaptureStream
    let capturedStream: MediaStream

    if (directCapture) {
      // Chrome / Firefox: direct captureStream on <video>
      console.log('[Stream] Using video.captureStream()')
      capturedStream = directCapture.call(video)
    } else {
      // Safari: draw video frames to a <canvas>, then canvas.captureStream().
      // Safari 17+ returns display-corrected (post-rotation) dimensions from
      // videoWidth/videoHeight. For older files with rotation metadata, the canvas
      // is sized to the natural dimensions and drawImage() draws the rotated frame
      // into that shape — the result is sent server-side as-is. Rotation correction
      // requires reading the container's rotation matrix which is not exposed by
      // the browser; the server must handle arbitrary orientations.
      console.log('[Stream] Using canvas intermediary for Safari')

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth || 640
      canvas.height = video.videoHeight || 480
      canvas.style.display = 'none'
      document.body.appendChild(canvas)
      const ctx = canvas.getContext('2d')!

      // Draw frames continuously.
      // Re-check videoWidth/videoHeight every frame to handle mid-stream resolution changes.
      const drawFrame = () => {
        if (!video.paused && !video.ended && fileVideoRef.current === video) {
          const vw = video.videoWidth || canvas.width
          const vh = video.videoHeight || canvas.height
          if (canvas.width !== vw || canvas.height !== vh) {
            canvas.width = vw
            canvas.height = vh
          }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          requestAnimationFrame(drawFrame)
        }
      }
      requestAnimationFrame(drawFrame)

      // captureStream on canvas — wrap in try/catch for CORS-tainted canvas (#12)
      try {
        capturedStream = canvas.captureStream(30) // 30 fps
      } catch (captureErr) {
        console.error('[Stream] canvas.captureStream() failed (possibly CORS-tainted):', captureErr)
        canvas.remove()
        setConnectionState('error')
        setErrorMessage('Cannot capture video stream. The video may be restricted by CORS.')
        setIsFileStream(false)
        isFileStreamRef.current = false
        return
      }

      // Store canvas ref for cleanup
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(video as any)._primatCanvas = canvas
    }

    // Strip audio tracks
    capturedStream.getAudioTracks().forEach((t: MediaStreamTrack) => {
      capturedStream.removeTrack(t)
      t.stop()
    })

    // Verify we have a valid video track
    const videoTracks = capturedStream.getVideoTracks()
    console.log('[Stream] capture tracks:', videoTracks.length, 'state:', videoTracks[0]?.readyState, 'settings:', JSON.stringify(videoTracks[0]?.getSettings()))
    if (videoTracks.length === 0 || videoTracks[0]?.readyState !== 'live') {
      console.error('[Stream] capture produced no live video track')
      setConnectionState('error')
      setErrorMessage('Could not capture video stream from file.')
      setIsFileStream(false)
      isFileStreamRef.current = false
      return
    }

    // Fix #10: Stop any existing camera stream before replacing with file stream.
    // Without this, the camera stays active (light on) during file streaming.
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }

    // Set as local stream — startStream will use this instead of requesting camera
    localStreamRef.current = capturedStream
    setLocalStream(capturedStream)

    // Update current time periodically.
    // Guard against post-teardown ticks (e.g. vitest jsdom env disposed before
    // the interval is cleared): bail out if window/fileVideoRef are gone.
    fileTimeUpdateIntervalRef.current = setInterval(() => {
      if (typeof window === 'undefined') {
        if (fileTimeUpdateIntervalRef.current) {
          clearInterval(fileTimeUpdateIntervalRef.current)
          fileTimeUpdateIntervalRef.current = null
        }
        return
      }
      if (fileVideoRef.current) {
        setFileCurrentTime(fileVideoRef.current.currentTime)
      }
    }, 250)

    // Listen for video end to auto-stop.
    // PRI-388: the video LOOPS during warmup (video.loop=true). `onended` does
    // NOT fire while looping, so we detect a completed playthrough via a
    // 'seeked'-to-start / timeupdate wrap instead. Strategy:
    //   - Until inference is live, keep looping (frames keep flowing).
    //   - Once inference is live, disable looping at the next wrap so the clip
    //     plays out one final full pass and then ends -> auto-stop.
    // A hard safety cap stops the session after maxLoopMs even if inference
    // never goes live, so a broken session can't loop forever.
    const fileStartMs = performance.now()
    const maxLoopMs = 90_000 // never loop a short clip longer than 90s total
    let lastT = 0
    video.ontimeupdate = () => {
      const t = video.currentTime
      // Detect loop wrap (time jumped backwards to ~0).
      if (t + 0.25 < lastT) {
        const elapsed = performance.now() - fileStartMs
        if (inferenceLiveRef.current || elapsed > maxLoopMs) {
          // Inference is live (or we hit the safety cap): stop looping so this
          // pass plays to the end, then onended fires the auto-stop.
          video.loop = false
        }
      }
      lastT = t
    }
    // Use a short delay so the MediaRecorder has time to flush its final chunk
    // before cleanup() stops it (fix #13).
    video.onended = () => {
      console.log('[Stream] File video ended, auto-stopping after flush delay')
      setTimeout(() => stopStream(), 500)
    }

    // Delegate to the existing startStream which handles all WebRTC/signalling setup
    // Delegate to startStream — if it fails, the error state propagates via connectionState
    try {
      await startStream(prompt, jwtToken, userPlan, useSam)
      console.log('[Stream] File stream started successfully, connectionState:', connectionStateRef.current)
    } catch (err) {
      console.error('[Stream] startStream failed for file stream:', err)
      setConnectionState('error')
      setErrorMessage('Failed to start file stream.')
      setIsFileStream(false)
      isFileStreamRef.current = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startStream, stopStream, cancelPreConnect])

  // ── Stream stop ─────────────────────────────────────────────────────────


  // Pre-warm TURN credentials. Call on source selection so they're ready by submit time.
  // Credentials expire after 5 min on the server; we cache for 4 min locally.
  const prewarmTurn = useCallback(async (jwtToken: string | null) => {
    // PRI-496 Lane B: public plane gets ice_servers on the create response —
    // nothing to pre-warm (and the endpoint is internal-plane only).
    if (resolveStreamPlane() === 'public') return
    // Skip if we have a fresh cache (< 4 minutes old)
    if (cachedTurnRef.current && (Date.now() - cachedTurnRef.current.fetchedAt) < 240_000) return
    try {
      const result = await getTurnCredentials(jwtToken)
      if (!result.failed && result.servers) {
        cachedTurnRef.current = { servers: result.servers, fetchedAt: Date.now() }
        console.log('[Stream] TURN pre-warmed, servers:', result.servers.length)
      } else if (result.failed) {
        console.warn('[Stream] TURN pre-warm failed — STUN-only fallback will apply if stream starts now')
        try { trackStreamError({ error_code: 'turn_fetch_failed', user_plan: userPlanRef.current }) } catch { /* non-fatal */ }
        Sentry.addBreadcrumb({ category: 'webrtc', message: 'TURN pre-warm failed — STUN-only fallback', level: 'warning' })
      }
    } catch { /* non-fatal */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const leaveQueue = useCallback(() => {
    signallingRef.current?.disconnect()
    signallingRef.current = null
    setConnectionState('idle')
    setQueue(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reset = useCallback(() => {
    setConnectionState('idle')
    setErrorMessage(null)
    setStopReason(null)
    setLastStopDuration_s(null)
    setTerminalUsage(null)
    setQueue(null)
    setMetrics(INITIAL_METRICS)
    setMetering(INITIAL_METERING)
    setRemoteStream(null)
    mediaRecorderRef.current = null
    recordingBlobRef.current = null
    setRecordingBlob(null)
    setNarrativeEntry(null)
    setNarrativeSummary(null)
    // Reset timing anchors for next session
    serverAnchorMsRef.current = null
    browserAnchorMsRef.current = null
    recordingStartedAtRef.current = 0
    if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── beforeunload cleanup ────────────────────────────────────────────────

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (signallingRef.current) {
        signallingRef.current.send({ type: 'stop' })
        signallingRef.current.disconnect()
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop())
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      handleBeforeUnload()
      // Clear file-stream interval on unmount so the 250ms tick doesn't fire
      // after the host environment is torn down (caused unhandled errors in
      // vitest CI — "window is not defined" when setFileCurrentTime ran
      // post-teardown).
      if (fileTimeUpdateIntervalRef.current) {
        clearInterval(fileTimeUpdateIntervalRef.current)
        fileTimeUpdateIntervalRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getRecordingBlob = useCallback((): Promise<Blob | null> => {
    return new Promise(resolve => {
      if (recordingBlobRef.current) { resolve(recordingBlobRef.current); return }
      let waited = 0
      const check = setInterval(() => {
        waited += 200
        if (recordingBlobRef.current) { clearInterval(check); resolve(recordingBlobRef.current) }
        else if (waited >= 4000) { clearInterval(check); resolve(null) }
      }, 200)
    })
  }, [])

  return {
    connectionState,
    cameraPermission,
    devices,
    selectedDeviceId,
    localStream,
    remoteStream,
    metering,
    queue,
    metrics,
    errorMessage,
    stopReason,
    lastStopDuration_s,
    terminalUsage,
    inferenceResult,
    narrativeEntry,
    promptChangeForNarrative,
    narrativeSummary,
    recordingBlob,
    recordingMaxMinutes,
    isFileStream,
    fileDuration,
    fileCurrentTime,
    isFrontCamera,
    connectProgress,
    promptUpdateError,
    combinedPrompt,
     
    setupTiming: setupTimingRef.current,
    selectDevice,
    releaseCamera,
    reacquireCamera,
    preConnect,
    cancelPreConnect,
    requestCameraAccess,
    frozenFrameDataUrl,
    prewarmTurn,
    startStream,
    startFileStream,
    stopStream,
    sendPromptUpdate,
    leaveQueue,
    reset,
    getRecordingBlob,
  }
}
